import { GroceryFilters } from "@/components/GroceryFilters";
import { emptyGroceryFilters, groceryFilterOptions, matchesGroceryFilters } from "@/lib/groceryFilters";
import FoodSupplierDisclosure from "@/components/FoodSupplierDisclosure";
import { useTown } from "@/hooks/use-town";
import "@/styles/food-ordering.css";
import { useState, useEffect, useRef, useCallback, useMemo, type ReactElement } from "react";
import { useRoute, useLocation } from "wouter";
import { withCheckoutAttempt, checkoutAttemptStorageKey, useGetOrderStore, getGetOrderStoreQueryKey, useGetReviews, getGetReviewsQueryKey } from "@workspace/api-client-react";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { useAnalytics } from "@/hooks/use-analytics";
import FeedAdSlot from "@/components/FeedAdSlot";
import { KindnessModal, useKindnessConfig, type KindnessDisplay } from "@/components/KindnessModal";
import { ToastAction } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { PillScroller } from "@/components/PillScroller";
import {
  ShoppingCart, Plus, Minus, Trash2, Clock, Truck, Package,
  ArrowLeft, ChevronDown, ChevronUp, X, Calendar, Check, MapPin, Loader2, Banknote, CreditCard,
  Info, Phone, Map as MapIcon, AlertTriangle, Search, Tag, LayoutGrid, List, Moon, Star,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ELEVATION, FOCUS_RING, MenuItemCard, RADII, TYPE,
  deriveCatalogueMode, effectiveMinOrderPence, formatOfferWithMin, type MenuItemLike,
} from "@/components/hub";

const SHOP_LOCAL_CATEGORIES = new Set(["Grocery", "Groceries", "Convenience Store", "Off License", "Off license, convenience store", "Convenience", "Retail", "General Store", "Off Licence", "Newsagent", "Pharmacy", "Hot Food"]);

function isShopLocalStore(store: { category?: string; categories?: string[] | null } | null | undefined): boolean {
  if (!store) return false;
  if (SHOP_LOCAL_CATEGORIES.has(store.category ?? "")) return true;
  return (store.categories ?? []).some((c) => SHOP_LOCAL_CATEGORIES.has(c));
}

// How many seconds before closing to show the countdown banner
const CLOSING_WARN_SECS = 20 * 60; // 20 minutes
// Minimum seconds remaining to allow checkout — if less, disable pay button
const CHECKOUT_MIN_SECS = 60;

function useClosingCountdown(closesAtTimestamp: string | null | undefined) {
  const [secsLeft, setSecsLeft] = useState<number | null>(null);

  useEffect(() => {
    if (!closesAtTimestamp) { setSecsLeft(null); return; }
    function tick() {
      const diff = Math.floor((new Date(closesAtTimestamp!).getTime() - Date.now()) / 1000);
      setSecsLeft(diff > 0 ? diff : 0);
    }
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [closesAtTimestamp]);

  return secsLeft;
}

function fmtCountdown(secs: number) {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function ClosingTimerBanner({ secsLeft }: { secsLeft: number }) {
  const urgent = secsLeft < 120;
  const blocked = secsLeft < CHECKOUT_MIN_SECS;
  return (
    <div className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-2.5 rounded-full shadow-lg text-sm font-semibold transition-colors ${
      blocked ? "bg-red-600 text-white" : urgent ? "bg-amber-500 text-white" : "bg-amber-50 dark:bg-amber-900/20 border border-amber-300 text-amber-800 dark:text-amber-300"
    }`}>
      <Clock size={15} />
      {blocked
        ? "Store now closed — checkout unavailable"
        : `Store closes in ${fmtCountdown(secsLeft)} — complete your order!`}
    </div>
  );
}

type SelectedOption = { groupId: number; groupName: string; optionId: number; optionName: string; pricePence: number };

type CartItem = {
  cartKey: string;
  menuItemId: number;
  name: string;
  pricePence: number;
  quantity: number;
  note?: string;
  selectedOptions?: SelectedOption[];
  optionsSummary?: string;
  multiBuyQty?: number | null;
  multiBuyPricePence?: number | null;
  isFreeItem?: boolean;
  isEndToEnd?: boolean;
  isUpsell?: boolean;
};

type ActiveOffer = { title: string; type: string; value: number; minOrderPence: number; freeItemName?: string | null; freeItemMenuItemId?: number | null; freeItemMode?: string; freeItemCategoryId?: number | null; freeItemPoolItemIds?: number[]; freeItemPickCount?: number; includedItemIds?: number[]; includedSectionIds?: number[]; excludedOptionIds?: number[]; bogofBuyQty?: number; bogofFreeQty?: number; bogofStack?: boolean };

type OptionChoice = { id: number; name: string; pricePence: number; available: boolean };

type OptionGroup = {
  id: number;
  name: string;
  required: boolean;
  multiSelect: boolean;
  minSelections: number;
  maxSelections: number | null;
  dependsOnOptionId: number | null;
  choices: OptionChoice[];
};

function pence(p: number) { return `£${(p / 100).toFixed(2)}`; }

const ADDRESSES_KEY = "order_delivery_addresses";
type SavedAddress = { address: string; postcode: string };

function calcItemTotal(item: CartItem): number {
  if (item.multiBuyQty && item.multiBuyPricePence && item.quantity >= item.multiBuyQty) {
    const bundles = Math.floor(item.quantity / item.multiBuyQty);
    return bundles * item.multiBuyPricePence + (item.quantity % item.multiBuyQty) * item.pricePence;
  }
  return item.pricePence * item.quantity;
}

type MixMatchDeal = { id: number; name: string; qty: number; pricePence: number; active: boolean; itemIds: number[] };

function calcMixMatchSavings(cart: CartItem[], deals: MixMatchDeal[]): number {
  let savings = 0;
  for (const deal of deals.filter((d) => d.active)) {
    const qualifying = cart.filter((ci) => deal.itemIds.includes(ci.menuItemId));
    const totalQty = qualifying.reduce((s, i) => s + i.quantity, 0);
    const bundles = Math.floor(totalQty / deal.qty);
    if (bundles === 0) continue;
    const units: number[] = [];
    for (const ci of qualifying) {
      for (let j = 0; j < ci.quantity; j++) units.push(ci.pricePence);
    }
    units.sort((a, b) => b - a);
    const bundleUnits = units.slice(0, bundles * deal.qty);
    const normalCostForBundles = bundleUnits.reduce((s, p) => s + p, 0);
    savings += Math.max(0, normalCostForBundles - bundles * deal.pricePence);
  }
  return savings;
}

function calcBogofSavings(cart: CartItem[], offers: ActiveOffer[], menuItems: Array<{ id: number; sectionId: number }>): number {
  const itemSectionMap = new Map(menuItems.map((m) => [m.id, m.sectionId]));
  let savings = 0;
  for (const offer of offers.filter((o) => o.type === "bogof")) {
    const buyQty = Math.max(1, offer.bogofBuyQty ?? 2);
    const freeQty = Math.max(1, offer.bogofFreeQty ?? 1);
    const stack = offer.bogofStack !== false;
    const groupSize = buyQty + freeQty;
    const incSections: number[] = offer.includedSectionIds ?? [];
    const incItems: number[] = offer.includedItemIds ?? [];
    const excOpts: number[] = offer.excludedOptionIds ?? [];
    const qualifying = cart.filter((ci) => {
      if (ci.isFreeItem) return false;
      // Option-excluded line (e.g. a 16" pizza) is not eligible — matches the server.
      if (excOpts.length > 0 && (ci.selectedOptions ?? []).some((o) => o.optionId != null && excOpts.includes(o.optionId))) return false;
      if (incItems.length > 0) return incItems.includes(ci.menuItemId);
      if (incSections.length > 0) return incSections.includes(itemSectionMap.get(ci.menuItemId) ?? -1);
      return true;
    });
    const totalQty = qualifying.reduce((s, i) => s + i.quantity, 0);
    const bundleCount = stack ? Math.floor(totalQty / groupSize) : Math.min(1, Math.floor(totalQty / groupSize));
    if (bundleCount === 0) continue;
    const freeCount = bundleCount * freeQty;
    const units: number[] = [];
    for (const ci of qualifying) {
      for (let j = 0; j < ci.quantity; j++) units.push(ci.pricePence);
    }
    units.sort((a, b) => a - b);
    savings += units.slice(0, freeCount).reduce((s, p) => s + p, 0);
  }
  return savings;
}

// ─── Schedule Picker ──────────────────────────────────────────────────────────

type DayHours = { open: string; close: string };
type ParsedHours = Partial<Record<"mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun", DayHours | null>>;
const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;
const FULL_DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] as const;

function parseOpeningHoursLocal(raw: string | null | undefined): ParsedHours | null {
  if (!raw?.trim() || raw.trim()[0] !== "{") return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== "object" || Array.isArray(parsed) || parsed === null) return null;
    return parsed as ParsedHours;
  } catch { return null; }
}

function nextOpeningPayLabel(raw: string | null | undefined, now = new Date()): string {
  const hours = parseOpeningHoursLocal(raw);
  if (!hours) return "Shop closed — check opening hours";
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London", weekday: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(now);
  const part = (type: string) => parts.find(p => p.type === type)?.value ?? "";
  const today = DAY_KEYS.findIndex(day => day === part("weekday").toLowerCase());
  const currentMinutes = Number(part("hour")) * 60 + Number(part("minute"));
  if (today < 0 || !Number.isFinite(currentMinutes)) return "Shop closed — check opening hours";
  for (let offset = 0; offset <= 7; offset++) {
    const dayIndex = (today + offset) % 7;
    const opens = hours[DAY_KEYS[dayIndex]]?.open;
    if (!opens || !/^([01]\d|2[0-3]):[0-5]\d$/.test(opens)) continue;
    const [hour, minute] = opens.split(":").map(Number);
    if (offset === 0 && currentMinutes >= hour * 60 + minute) continue;
    return `Shop closed — opens at ${opens} on ${FULL_DAY_NAMES[dayIndex]}`;
  }
  return "Shop closed — check opening hours";
}

const DAY_LABELS: Array<[Exclude<keyof ParsedHours, undefined>, string]> = [
  ["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"],
];

/**
 * Seven rows for the "Hours & address" dialog — the same shape the app's
 * `formatOpeningHours` (mobile/app/order-store.tsx) produces, so both platforms
 * show the identical table.
 *
 * The stored times are UK wall-clock strings ("12:00"), not UTC instants — the
 * server reads them against Europe/London (api-server/src/lib/openingHours.ts),
 * so they are printed verbatim rather than converted. "Today" is resolved in
 * Europe/London so a browser in another zone still highlights the row a
 * Scottish shop is actually trading on.
 */
function formatOpeningHours(raw: string | null | undefined): Array<{ label: string; hours: string; isToday: boolean }> | null {
  if (!raw?.trim()) return null;
  if (raw.trim()[0] !== "{") return [{ label: "", hours: raw.trim(), isToday: false }];
  const parsed = parseOpeningHoursLocal(raw);
  if (!parsed) return null;
  const today = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", weekday: "short" })
    .format(new Date()).replace(/[^a-zA-Z]/g, "").toLowerCase();
  return DAY_LABELS.map(([key, label]) => {
    const day = parsed[key];
    return {
      label,
      hours: day?.open && day.close ? `${day.open} – ${day.close}` : "Closed",
      isToday: key === today,
    };
  });
}

function generateTimeSlots(date: Date, hours: ParsedHours | null, prepMins: number, isToday: boolean): string[] {
  const dayKey = DAY_KEYS[date.getDay()] as keyof ParsedHours;
  let openHH = 7, openMM = 0, closeHH = 23, closeMM = 0;
  if (hours && dayKey in hours) {
    const dayH = hours[dayKey];
    if (!dayH) return [];
    const [oh, om] = dayH.open.split(":").map(Number);
    const [ch, cm] = dayH.close.split(":").map(Number);
    openHH = oh; openMM = om; closeHH = ch; closeMM = cm;
  }
  const now = new Date();
  const minTime = new Date(now.getTime() + prepMins * 60_000);
  const minHH = isToday ? minTime.getHours() : -1;
  const minMM = isToday ? minTime.getMinutes() : 0;
  const slots: string[] = [];
  for (let h = openHH; h <= closeHH; h++) {
    for (let m = 0; m < 60; m += 30) {
      if (h === openHH && m < openMM) continue;
      if (h > closeHH || (h === closeHH && m >= closeMM)) break;
      if (isToday && (h < minHH || (h === minHH && m < Math.ceil(minMM / 30) * 30))) continue;
      slots.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return slots;
}

function buildScheduledFor(date: Date, slotHHMM: string): string {
  const [h, m] = slotHHMM.split(":").map(Number);
  const d = new Date(date);
  d.setHours(h, m, 0, 0);
  return d.toISOString();
}

function getOrderDates(): Date[] {
  const dates: Date[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = 0; i < 7; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    dates.push(d);
  }
  return dates;
}

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function WebSchedulePicker({ openingHoursRaw, prepMins, onChange }: {
  openingHoursRaw: string | null | undefined;
  prepMins: number;
  onChange: (iso: string) => void;
}) {
  const hours = parseOpeningHoursLocal(openingHoursRaw);
  const dates = getOrderDates();
  const [selectedDate, setSelectedDate] = useState<Date>(dates[0]);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  const todayStr = new Date().toDateString();
  const isToday = selectedDate.toDateString() === todayStr;
  const slots = generateTimeSlots(selectedDate, hours, prepMins, isToday);

  function handleDateSelect(d: Date) {
    setSelectedDate(d);
    setSelectedSlot(null);
    onChange("");
  }

  function handleSlotSelect(slot: string) {
    setSelectedSlot(slot);
    onChange(buildScheduledFor(selectedDate, slot));
  }

  return (
    <div className="space-y-3 mt-1">
      {/* Day row */}
      <div className="overflow-x-auto flex flex-nowrap gap-2 pb-1" style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}>
        {dates.map((d, i) => {
          const isSel = d.toDateString() === selectedDate.toDateString();
          const label = i === 0 ? "Today" : i === 1 ? "Tomorrow"
            : `${DAY_NAMES[d.getDay()]} ${d.getDate()} ${MONTH_NAMES[d.getMonth()]}`;
          return (
            <button
              key={d.toISOString()}
              type="button"
              onClick={() => handleDateSelect(d)}
              className={`whitespace-nowrap flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold border-2 transition-colors ${
                isSel ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary/40"
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Time slots */}
      {slots.length === 0 ? (
        <p className="text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2">
          {isToday ? "No more slots today — try another day" : "Store is closed on this day"}
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {slots.map((slot) => {
            const isSel = slot === selectedSlot;
            return (
              <button
                key={slot}
                type="button"
                onClick={() => handleSlotSelect(slot)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold border-2 transition-colors ${
                  isSel ? "bg-primary text-white border-primary" : "bg-white text-slate-700 border-slate-200 hover:border-primary/40"
                }`}
              >
                {slot}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Option Picker Modal ───────────────────────────────────────────────────────

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

function OptionPickerModal({
  item, groups, basePrice, onAdd, onClose, excludedOptionLabel,
}: {
  item: { id: number; name: string; imageUrl?: string | null };
  groups: OptionGroup[];
  basePrice: number;
  onAdd: (selections: SelectedOption[], note: string) => void;
  onClose: () => void;
  excludedOptionLabel?: Map<number, string>;
}) {
  const [selections, setSelections] = useState<SelectedOption[]>([]);
  const [multiQtys, setMultiQtys] = useState<Record<number, number>>({});
  const [itemNote, setItemNote] = useState("");
  const dialogRef = useRef<HTMLDivElement>(null);

  /* Focus the dialog ONCE, when it opens.
     This used to sit in the same effect as the key handler below, whose
     dependency is `onClose` — an inline arrow at the call site, so a fresh
     identity on every parent render. The parent re-renders once a second while
     the closing countdown ticks, so `el.focus()` fired every second and pulled
     focus off whatever the customer was typing. On a phone that dismisses the
     keyboard: reported as "the keyboard comes up then instantly disappears" in
     Special instructions. Focusing a dialog is a mount concern, not a
     re-render one. */
  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") { onClose(); return; }
      if (e.key !== "Tab") return;
      const focusable = Array.from(el!.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  // Groups whose parent option is currently selected (or have no dependency)
  const selectedOptionIds = new Set([
    ...selections.map((s) => s.optionId),
    ...Object.entries(multiQtys)
      .filter(([, quantity]) => quantity > 0)
      .map(([optionId]) => Number(optionId)),
  ]);
  const visibleGroups = groups.filter(
    (g) => g.dependsOnOptionId == null || selectedOptionIds.has(g.dependsOnOptionId)
  );

  // Returns IDs of all groups that depend on the given option being selected
  function dependentGroupIds(optionId: number): Set<number> {
    const ids = new Set<number>();
    for (const g of groups) {
      if (g.dependsOnOptionId === optionId) ids.add(g.id);
    }
    return ids;
  }

  function clearDependentGroups(optionId: number, prevSelections: SelectedOption[], prevQtys: Record<number, number>) {
    const depIds = dependentGroupIds(optionId);
    if (depIds.size === 0) return { selections: prevSelections, qtys: prevQtys };
    const nextSelections = prevSelections.filter((s) => !depIds.has(s.groupId));
    const nextQtys = { ...prevQtys };
    for (const g of groups) {
      if (depIds.has(g.id)) g.choices.forEach((c) => { delete nextQtys[c.id]; });
    }
    return { selections: nextSelections, qtys: nextQtys };
  }

  function multiGroupCount(groupId: number) {
    const g = groups.find((gr) => gr.id === groupId);
    if (!g) return 0;
    return g.choices.reduce((s, c) => s + (multiQtys[c.id] ?? 0), 0);
  }
  function groupCount(groupId: number) {
    const g = groups.find((gr) => gr.id === groupId);
    return g?.multiSelect ? multiGroupCount(groupId) : selections.filter((s) => s.groupId === groupId).length;
  }

  function toggleOption(group: OptionGroup, choice: OptionChoice) {
    if (group.multiSelect) return;
    const alreadySelected = selections.some((s) => s.groupId === group.id && s.optionId === choice.id);
    if (alreadySelected && group.minSelections === 0 && !group.required) {
      // Deselecting — clear this group and any groups that depended on this option
      const { selections: nextSel, qtys: nextQtys } = clearDependentGroups(choice.id, selections, multiQtys);
      setSelections(nextSel.filter((s) => s.groupId !== group.id));
      setMultiQtys(nextQtys);
    } else {
      // Switching selection — clear dependent groups of the previously selected option in this group
      const prevChoice = selections.find((s) => s.groupId === group.id);
      let nextSel = selections;
      let nextQtys = multiQtys;
      if (prevChoice && prevChoice.optionId !== choice.id) {
        ({ selections: nextSel, qtys: nextQtys } = clearDependentGroups(prevChoice.optionId, selections, multiQtys));
      }
      setSelections([
        ...nextSel.filter((s) => s.groupId !== group.id),
        { groupId: group.id, groupName: group.name, optionId: choice.id, optionName: choice.name, pricePence: choice.pricePence },
      ]);
      setMultiQtys(nextQtys);
    }
  }
  function incrementQty(group: OptionGroup, choice: OptionChoice) {
    const max = group.maxSelections;
    if (max && max > 0 && multiGroupCount(group.id) >= max) return;
    setMultiQtys((prev) => ({ ...prev, [choice.id]: (prev[choice.id] ?? 0) + 1 }));
  }
  function decrementQty(choice: OptionChoice) {
    setMultiQtys((prev) => {
      const cur = prev[choice.id] ?? 0;
      if (cur <= 0) return prev;
      return { ...prev, [choice.id]: cur - 1 };
    });
  }
  function buildFinalSelections(): SelectedOption[] {
    const visibleGroupIds = new Set(visibleGroups.map((g) => g.id));
    const multi: SelectedOption[] = visibleGroups
      .filter((g) => g.multiSelect)
      .flatMap((g) =>
        g.choices.flatMap((c) =>
          Array.from({ length: multiQtys[c.id] ?? 0 }, () => ({
            groupId: g.id, groupName: g.name, optionId: c.id, optionName: c.name, pricePence: c.pricePence,
          }))
        )
      );
    return [...selections.filter((s) => visibleGroupIds.has(s.groupId)), ...multi];
  }

  const isSelected = (optionId: number) => selections.some((s) => s.optionId === optionId);
  const requiredSatisfied = visibleGroups.every((g) => {
    const min = g.minSelections ?? 0;
    if (min === 0 && !g.required) return true;
    return groupCount(g.id) >= (min > 0 ? min : 1);
  });
  const optionsTotal =
    selections.reduce((sum, s) => sum + s.pricePence, 0) +
    visibleGroups.filter((g) => g.multiSelect).flatMap((g) => g.choices)
      .reduce((sum, c) => sum + (multiQtys[c.id] ?? 0) * c.pricePence, 0);

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/35 backdrop-blur-sm" onClick={onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="item-options-title"
        tabIndex={-1}
        className="bg-white w-full sm:max-w-[560px] rounded-t-[32px] sm:rounded-[32px] max-h-[94dvh] shadow-[0_24px_100px_#0f172a40] overflow-hidden flex flex-col outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 shrink-0">
          <span className="text-xs font-semibold tracking-wide text-slate-500">Customise your order</span>
          <button onClick={onClose} aria-label="Close" className="w-11 h-11 shrink-0 grid place-items-center rounded-full bg-slate-100"><X size={20} /></button>
        </div>
        <div className="overflow-y-auto flex-1 px-5 pb-6 space-y-6">
          {item.imageUrl && <img src={item.imageUrl} alt={item.name} className="w-full aspect-[2.15/1] object-cover rounded-[20px] mt-2" />}
          <div className="pt-4"><h3 id="item-options-title" className="text-[28px] leading-tight font-bold tracking-[-1px] text-[#1e3a5f]">{item.name}</h3><p className="text-base text-slate-600 mt-2">{pence(basePrice)}</p></div>
          {visibleGroups.map((group) => (
            <div key={group.id} className="border-t border-slate-100 pt-5">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <h4 className="font-bold text-[#1e3a5f] text-base">{group.name}</h4>
                {((group.minSelections ?? 0) >= 1 || group.required)
                  ? <span className="text-xs bg-[#fff8e6] text-[#8a5a00] px-2 py-0.5 rounded-full font-medium">Required</span>
                  : <span className="text-xs text-slate-400">Optional</span>}
                {group.multiSelect && (
                  <span className="text-xs text-slate-400">
                    · {group.maxSelections && group.maxSelections > 0
                      ? `Choose up to ${group.maxSelections}`
                      : "Choose any number"}
                  </span>
                )}
              </div>
              <div className="space-y-1.5">
                {group.choices.filter((c) => c.available).map((choice) => {
                  if (group.multiSelect) {
                    const qty = multiQtys[choice.id] ?? 0;
                    return (
                      <div key={choice.id}
                        className={`w-full flex items-center justify-between min-h-14 px-4 py-3 rounded-2xl border transition-all ${qty > 0 ? "border-[#f0a500] bg-[#fff8e6]" : "border-slate-200"}`}
                      >
                        <span className="text-sm font-medium text-slate-800">{choice.name}{excludedOptionLabel?.has(choice.id) && <span className="ml-2 text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full align-middle">Not in {excludedOptionLabel.get(choice.id)}</span>}</span>
                        <div className="flex items-center gap-2 ml-2">
                          {choice.pricePence > 0 && (
                            <span className="text-sm font-semibold text-primary">+{pence(choice.pricePence)}</span>
                          )}
                          <button type="button" onClick={() => decrementQty(choice)}
                            className="w-11 h-11 shrink-0 rounded-xl border border-slate-300 flex items-center justify-center hover:bg-slate-100 disabled:opacity-30 transition-colors"
                            disabled={qty === 0}>
                            <Minus size={12} />
                          </button>
                          <span className="text-sm font-bold w-5 text-center tabular-nums">{qty}</span>
                          <button type="button" onClick={() => incrementQty(group, choice)}
                            className="w-11 h-11 shrink-0 rounded-xl border border-[#f0a500] bg-[#fff8e6] flex items-center justify-center hover:bg-primary/20 transition-colors">
                            <Plus size={12} className="text-primary" />
                          </button>
                        </div>
                      </div>
                    );
                  }
                  const selected = isSelected(choice.id);
                  return (
                    <button
                      key={choice.id}
                      onClick={() => toggleOption(group, choice)}
                      className={`w-full flex items-center justify-between min-h-14 px-4 py-3 rounded-2xl border transition-all ${selected ? "border-[#f0a500] bg-[#fff8e6]" : "border-slate-200 hover:border-primary/40"}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-colors ${selected ? "border-primary bg-primary" : "border-slate-300"}`}>
                          {selected && <Check size={11} className="text-white" />}
                        </div>
                        <span className="text-sm font-medium text-slate-800">{choice.name}{excludedOptionLabel?.has(choice.id) && <span className="ml-2 text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full align-middle">Not in {excludedOptionLabel.get(choice.id)}</span>}</span>
                      </div>
                      {choice.pricePence > 0 && (
                        <span className="text-sm font-semibold text-primary ml-2">+{pence(choice.pricePence)}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="px-5 pb-4 pt-2">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1.5">
            Special instructions <span className="font-normal normal-case text-slate-400">(optional)</span>
          </label>
          <input
            type="text"
            value={itemNote}
            onChange={(e) => setItemNote(e.target.value)}
            maxLength={150}
            placeholder="Add a note for the shop (optional)…"
            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/60"
          />
        </div>
        <div className="p-5 border-t border-slate-100 shrink-0 bg-white">
          <Button
            className="w-full min-h-[60px] rounded-[20px] bg-primary hover:bg-primary/90 font-bold"
            disabled={!requiredSatisfied}
            onClick={() => { onAdd(buildFinalSelections(), itemNote.trim()); setItemNote(""); }}
          >
            Add to basket — {pence(basePrice + optionsTotal)}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Cart Panel ────────────────────────────────────────────────────────────────

/** Drag is limited to the handle, so scrolling food or forms never dismisses a sheet. */
function FoodSheetHandle({onDismiss}: {onDismiss: () => void}) {
  const start = useRef<number | null>(null);
  const reset = (element: HTMLElement) => { element.closest<HTMLElement>(".food-sheet")?.style.setProperty("--sheet-drag", "0px"); start.current = null; };
  return <div aria-hidden="true" className="food-sheet-handle" onPointerDown={event => { start.current = event.clientY; event.currentTarget.setPointerCapture(event.pointerId); }} onPointerMove={event => { if (start.current !== null) event.currentTarget.closest<HTMLElement>(".food-sheet")?.style.setProperty("--sheet-drag", `${Math.max(0, event.clientY - start.current)}px`); }} onPointerUp={event => { const distance = start.current === null ? 0 : event.clientY - start.current; reset(event.currentTarget); if (distance > 90) onDismiss(); }} onPointerCancel={event => reset(event.currentTarget)}><span /></div>;
}

function NoteInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  if (!open && !value) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="min-h-11 text-xs text-slate-400 hover:text-primary mt-1.5 flex items-center gap-1 transition-colors"
      >
        Add note
      </button>
    );
  }
  if (!open && value) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="min-h-11 text-xs text-amber-700 bg-amber-50 rounded-lg px-2 py-1 mt-1.5 flex items-center gap-1 hover:bg-amber-100 transition-colors w-full text-left truncate"
      >
        <Tag size={12} aria-hidden />
        <span className="truncate">{value}</span>
      </button>
    );
  }
  return (
    <div className="mt-1.5">
      <input
        autoFocus
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => { onChange(draft.trim()); setOpen(false); }}
        onKeyDown={(e) => {
          if (e.key === "Enter") { onChange(draft.trim()); setOpen(false); }
          if (e.key === "Escape") { setDraft(value); setOpen(false); }
        }}
        maxLength={150}
        placeholder="Add a note for the shop (optional)…"
        className="min-h-11 w-full border border-amber-300 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-amber-400 bg-amber-50"
      />
    </div>
  );
}

function CartPanel({
  cart, store, deals, activeOffers, menuItems, onAdd, onRemove, onUpdateNote, onCheckout, loading, checkoutBlockedByTimer, isTestMode, initialDiscountCode, onNameChange, onPhoneChange, compactMobileBasket, closedPayLabel,
  onMultiOrderStart, inCompanionMode, multiAnchorName, onMultiOrderCheckout, guestEmail, onEmailChange, showGuestEmail,
  kindnessContributionPence, onKindnessChange, onKindnessRemove,
  kindnessCreditPreviewPence,
}: {
  /** Random Act of Kindness contribution chosen for this checkout (0 = none). */
  kindnessContributionPence?: number;
  /** Re-open the kindness modal to change the amount. */
  onKindnessChange?: () => void;
  /** Clear the contribution. */
  onKindnessRemove?: () => void;
  /** Recipient "paid it forward" credit (sized + fetched at the page level, shown
   *  once while browsing). Display-only; the server reserves/applies it. */
  kindnessCreditPreviewPence?: number;
  cart: CartItem[];
  compactMobileBasket?: boolean;
  closedPayLabel?: string;
  checkoutBlockedByTimer?: boolean;
  isTestMode?: boolean;
  initialDiscountCode?: string;
  onNameChange?: (name: string) => void;
  onPhoneChange?: (phone: string) => void;
  guestEmail?: string;
  onEmailChange?: (email: string) => void;
  showGuestEmail?: boolean;
  /** Called when user clicks "Add from another store" (anchor mode) */
  onMultiOrderStart?: () => void;
  /** When true this is the companion store — show simplified checkout */
  inCompanionMode?: boolean;
  /** Name of the anchor store shown in companion mode banner */
  multiAnchorName?: string;
  /** Called in companion mode instead of normal checkout */
  onMultiOrderCheckout?: () => void;
  store: {
    id: number; name: string; deliveryFeePence: number; minOrderPence: number;
    minOrderBreakfastPence?: number | null;
    minOrderLunchPence?: number | null;
    minOrderEveningPence?: number | null;
    offersDelivery: boolean; offersCollection: boolean;
    stripeAccountId?: string | null; stripeOnboardingComplete?: boolean | null;
    serviceChargePence?: number | null; bagChargePence?: number | null;
    acceptsCash?: boolean | null; useHubDrivers?: boolean | null;
    isOpen?: boolean | null;
    closesAtTimestamp?: string | null;
    hasDeliveryZones?: boolean;
    openingHours?: string | null;
    deliveryTimeMins?: number | null;
    collectionTimeMins?: number | null;
  };
  deals: MixMatchDeal[];
  activeOffers: ActiveOffer[];
  menuItems: Array<{ id: number; name: string; pricePence: number; sectionId: number; imageUrl?: string | null }>;
  onAdd: (item: CartItem) => void;
  onRemove: (cartKey: string) => void;
  onUpdateNote: (cartKey: string, note: string) => void;
  onCheckout: (opts: { fulfilmentType: string; deliveryAddress: string; customerPostcode: string; scheduledFor: string; customerName: string; customerPhone: string; notes: string; deliveryFeePence: number; serviceChargePence: number; bagChargePence: number; totalPence: number; subtotalPence: number; paymentMethod: "card" | "cash"; pledgedTipPence?: number; discountCode?: string }) => void;
  loading: boolean;
}) {
  const { deliveryPostcode } = useTown();
  const [fulfilment, setFulfilment] = useState<"delivery" | "collection">(store.offersDelivery ? "delivery" : "collection");
  const [showMultiStoreInfo, setShowMultiStoreInfo] = useState(false);
  const [address, setAddress] = useState("");
  const [postcode, setPostcode] = useState(cart.some(item => item.isEndToEnd) ? "" : deliveryPostcode ?? "");
  const [deliveryFeeOverride, setDeliveryFeeOverride] = useState<number | null>(null);
  const [zoneLabel, setZoneLabel] = useState<string | null>(null);
  const [feeChecked, setFeeChecked] = useState(false);
  const [feeChecking, setFeeChecking] = useState(false);
  const [outOfArea, setOutOfArea] = useState(false);
  const [zoneFeeMatched, setZoneFeeMatched] = useState(false);
  const [locating, setLocating] = useState(false);
  const [geoCoords, setGeoCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [scheduleMode, setScheduleMode] = useState<"asap" | "scheduled">("asap");
  const [scheduledFor, setScheduledFor] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [checkoutValidationAttempted, setCheckoutValidationAttempted] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const addressFieldRef = useRef<HTMLDivElement>(null);
  function focusMissingCheckoutField(field: "address" | "name" | "phone") {
    requestAnimationFrame(() => {
      const addressInputs = Array.from(addressFieldRef.current?.querySelectorAll("input") ?? []);
      const target = field === "name" ? nameInputRef.current : field === "phone" ? phoneInputRef.current : addressInputs.find(input => !input.value.trim()) ?? addressInputs[0] ?? addressFieldRef.current;
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
      target?.focus({ preventScroll: true });
    });
  }

  const [paymentMethod, setPaymentMethod] = useState<"card" | "cash">("card");
  const [pledgedTipPence, setPledgedTipPence] = useState(0);
  const [savedAddresses, setSavedAddresses] = useState<SavedAddress[]>([]);
  const [showSavedAddresses, setShowSavedAddresses] = useState(false);

  // Server-authoritative pricing breakdown — fetched from POST /api/cart/price.
  // Screens render this instead of computing totals locally so that any new fee
  // added to the server appears everywhere automatically.
  const [serverPricing, setServerPricing] = useState<{
    lines: Array<{ type: string; label: string; pence: number; negative: boolean }>;
    subtotalPence: number;
    customerSubtotalPence?: number;
    customerPaidPence: number;
    merchantTotalPence: number;
  } | null>(null);
  const pricingDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Random Act of Kindness — RECIPIENT side ───────────────────────────────
  // The recipient credit is now fetched ONCE at the page level (OrderStoreDetail)
  // and the amount is passed in via `kindnessCreditPreviewPence`. The thank-you
  // modal is popped there too (once, while browsing), so CartPanel only renders
  // the display-only "−£X" line / reduced total — no fetch, no second pop.

  // Checkout upsells — "Fancy any of these?" shown right before pay. Server-priced
  // (each added line carries isUpsell:true so the API applies the shop's offer price).
  const [upsells, setUpsells] = useState<Array<{ menuItemId: number; name: string; description: string | null; imageUrl: string | null; normalPricePence: number; offerPricePence: number; savingPence: number }>>([]);
  useEffect(() => {
    if (!store?.id || cart.length === 0) { setUpsells([]); return; }
    let cancelled = false;
    fetch(`/api/order/stores/${store.id}/checkout-upsells-public`)
      .then((r) => r.ok ? r.json() : { items: [] })
      .then((d: { items?: typeof upsells }) => { if (!cancelled) setUpsells(d.items ?? []); })
      .catch(() => { if (!cancelled) setUpsells([]); });
    return () => { cancelled = true; };
    // Refetch only when the store changes or the cart transitions to/from empty.
  }, [store?.id, cart.length === 0]);
  const upsellCartItemIds = new Set(cart.map((c) => c.menuItemId));
  const visibleUpsells = upsells.filter((u) => !upsellCartItemIds.has(u.menuItemId));

  const [discountCodeOpen, setDiscountCodeOpen] = useState(false);
  const [discountCodeInput, setDiscountCodeInput] = useState("");
  const [appliedCode, setAppliedCode] = useState<string | null>(null);
  const [codeOffer, setCodeOffer] = useState<{ discountPence: number; offerTitle: string } | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [codeChecking, setCodeChecking] = useState(false);

  // Auto-apply recovery code (via ?recover=) or a saved influencer referral code (from /r/:code)
  useEffect(() => {
    const savedRef = (() => { try { return localStorage.getItem("influencer_ref_code") || ""; } catch { return ""; } })();
    const codeToTry = initialDiscountCode || savedRef;
    if (!codeToTry || appliedCode) return;
    setDiscountCodeInput(codeToTry);
    const currentSubtotal = cart.filter(i => !i.isFreeItem).reduce((s, i) => s + calcItemTotal(i), 0);
    void (async () => {
      setCodeChecking(true);
      try {
        const authToken = localStorage.getItem("ayrshire_token");
        const r = await fetch(`/api/order/stores/${store.id}/validate-code?code=${encodeURIComponent(codeToTry)}&subtotal=${currentSubtotal}`, authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : undefined);
        const d = await r.json() as { valid: boolean; offerTitle?: string; discountPence?: number; error?: string };
        if (d.valid && d.discountPence != null && d.offerTitle) {
          setCodeOffer({ discountPence: d.discountPence, offerTitle: d.offerTitle });
          setAppliedCode(codeToTry);
          setDiscountCodeInput("");
        }
      } catch { /* non-critical */ } finally { setCodeChecking(false); }
    })();
  }, [initialDiscountCode]);
  const isCourierService = cart.some((item) => item.isEndToEnd);

  const lastCheckedPostcode = useRef("");
  const lookupAbortRef = useRef<AbortController | null>(null);
  // Keeps the raw input value in sync without depending on React's render cycle.
  // lookupZoneFee reads this ref so it always sees the current value even when
  // called from an event handler whose closure captured a stale `postcode` state.
  const postcodeRef = useRef(postcode);

  // The shared postcode is already known: check its real delivery zone before
  // showing a checkout total. Manual address/postcode changes retain their own checks.
  useEffect(() => {
    if (fulfilment !== "delivery" || !deliveryPostcode || postcode !== deliveryPostcode) return;
    const timer = setTimeout(() => { void lookupZoneFee(deliveryPostcode); }, 400);
    return () => clearTimeout(timer);
  }, [deliveryPostcode, postcode, fulfilment, store.id]);

  // Load saved delivery addresses from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(ADDRESSES_KEY);
      if (raw) setSavedAddresses(JSON.parse(raw) as SavedAddress[]);
    } catch {}
  }, []);

  // Fetch server-authoritative pricing whenever cart, fulfilment, zone fee or discount changes.
  useEffect(() => {
    if (pricingDebounceRef.current) clearTimeout(pricingDebounceRef.current);
    if (cart.length === 0) { setServerPricing(null); return; }
    // Drop the previous quote the moment the cart changes so the breakdown never
    // shows STALE server pricing (e.g. after adding an upsell): the Subtotal/Total
    // rows fall back to the live local calc — which already includes the new line —
    // until the fresh authoritative quote lands ~400ms later. Without this, a stale
    // or failed refetch left the totals frozen while the pay button (local) moved.
    setServerPricing(null);
    pricingDebounceRef.current = setTimeout(async () => {
      try {
        const priceToken = localStorage.getItem("ayrshire_token");
        const res = await fetch("/api/cart/price", {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(priceToken ? { Authorization: `Bearer ${priceToken}` } : {}) },
          body: JSON.stringify({
            storeId: store.id,
            items: cart.map(i => ({ menuItemId: i.menuItemId, quantity: i.quantity, selectedOptions: i.selectedOptions, isFreeItem: i.isFreeItem, isUpsell: i.isUpsell })),
            fulfilmentType: fulfilment,
            deliveryFeePence: deliveryFeeOverride,
            offerCode: appliedCode ?? undefined,
          }),
        });
        if (res.ok) setServerPricing(await res.json());
        else setServerPricing(null); // keep the live local fallback, never a stale quote
      } catch { setServerPricing(null); /* keep the live local fallback */ }
    }, 400);
    return () => { if (pricingDebounceRef.current) clearTimeout(pricingDebounceRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, fulfilment, deliveryFeeOverride, appliedCode]);

  function saveAddress(addr: string, pc: string) {
    if (!addr.trim() && !pc.trim()) return;
    const entry: SavedAddress = { address: addr.trim(), postcode: pc.trim() };
    try {
      const raw = localStorage.getItem(ADDRESSES_KEY);
      const prev: SavedAddress[] = raw ? (JSON.parse(raw) as SavedAddress[]) : [];
      const deduped = prev.filter((s) => !(s.address === entry.address && s.postcode === entry.postcode));
      const next = [entry, ...deduped].slice(0, 3);
      localStorage.setItem(ADDRESSES_KEY, JSON.stringify(next));
      setSavedAddresses(next);
    } catch {}
  }

  const PLATFORM_FEE_PENCE = (store as unknown as { platformFeePence?: number | null }).platformFeePence ?? 100;
  const HUB_DRIVER_FEE_PENCE = (store as unknown as { hubDriverFeePence?: number | null }).hubDriverFeePence ?? 0;
  const subtotal = cart.filter(i => !i.isFreeItem).reduce((s, i) => s + calcItemTotal(i), 0);
  const mixMatchSavings = calcMixMatchSavings(cart.filter(i => !i.isFreeItem), deals);
  const bogofSavings = calcBogofSavings(cart.filter(i => !i.isFreeItem), activeOffers, menuItems);

  // Free item offer logic
  const freeItemOffer = activeOffers.find(o => o.type === "free_item");
  const freeItemPool = useMemo<Array<{ menuItemId: number; name: string }>>(() => {
    if (!freeItemOffer) return [];
    const mode = freeItemOffer.freeItemMode ?? "single";
    if (mode === "single") {
      return (freeItemOffer.freeItemMenuItemId && freeItemOffer.freeItemName)
        ? [{ menuItemId: freeItemOffer.freeItemMenuItemId, name: freeItemOffer.freeItemName }]
        : [];
    }
    if (mode === "category") {
      return menuItems.filter(i => i.sectionId === freeItemOffer.freeItemCategoryId)
        .map(i => ({ menuItemId: i.id, name: i.name }));
    }
    const poolIds = new Set(freeItemOffer.freeItemPoolItemIds ?? []);
    return menuItems.filter(i => poolIds.has(i.id)).map(i => ({ menuItemId: i.id, name: i.name }));
  }, [freeItemOffer, menuItems]);
  const freeItemPickCount = Math.max(1, freeItemOffer?.freeItemPickCount ?? 1);
  const freeItemQualifies = freeItemOffer != null && freeItemPool.length > 0 && subtotal >= freeItemOffer.minOrderPence;
  const freeItemsInCart = cart.filter(i => i.isFreeItem);
  const freeItemInCart = freeItemsInCart.length > 0;
  const freeItemsRemaining = freeItemQualifies ? Math.max(0, freeItemPickCount - freeItemsInCart.length) : 0;
  // Auto-remove free items if subtotal drops below threshold
  useEffect(() => {
    if (!freeItemQualifies && freeItemInCart) {
      freeItemsInCart.forEach(fi => onRemove(fi.cartKey));
    }
  }, [freeItemQualifies, freeItemInCart, freeItemsInCart, onRemove]);
  const deliveryFee = fulfilment === "delivery" ? (deliveryFeeOverride ?? store.deliveryFeePence) : 0;
  const fulfilmentFee = isCourierService ? deliveryFee * 2 : deliveryFee;
  const serviceCharge = store.serviceChargePence ?? 0;
  const bagCharge = store.bagChargePence ?? 0;
  const ayrshireHubFee = PLATFORM_FEE_PENCE + (store.useHubDrivers && fulfilment === "delivery" ? HUB_DRIVER_FEE_PENCE : 0);
  // The platform Service Charge is added to the customer total for both card and cash orders.
  // For cash, the store collects it and the platform reclaims it from the store's next payout.
  const codeDiscountPence = codeOffer?.discountPence ?? 0;
  const subtotalAfterMix = Math.max(0, subtotal - mixMatchSavings - bogofSavings);
  // Only apply auto-offer when no code offer is active (mirrors server mutual exclusion)
  const bestAutoOffer = codeOffer ? null : activeOffers
    .filter(o => o.type !== "free_item" && o.type !== "bogof" && subtotalAfterMix >= o.minOrderPence)
    .sort((a, b) => {
      const discA = a.type === "percentage" ? Math.round(subtotalAfterMix * a.value / 100) : a.value;
      const discB = b.type === "percentage" ? Math.round(subtotalAfterMix * b.value / 100) : b.value;
      return discB - discA;
    })[0] ?? null;
  const autoOfferDiscountPence = bestAutoOffer
    ? bestAutoOffer.type === "percentage" ? Math.round(subtotalAfterMix * bestAutoOffer.value / 100) : bestAutoOffer.value
    : 0;
  const total = subtotal - mixMatchSavings - bogofSavings - autoOfferDiscountPence - codeDiscountPence + fulfilmentFee + serviceCharge + bagCharge + ayrshireHubFee;
  /* Shared with the shop header so the minimum a customer is *shown* is the
     same one the basket *enforces*. For a scheduled order the minimum is
     evaluated against the scheduled slot, not the current clock. */
  const effectiveMinOrder = effectiveMinOrderPence(
    store,
    scheduleMode === "scheduled" && scheduledFor ? new Date(scheduledFor) : undefined,
  );
  /* Compared against the same figure the server compares: the food subtotal.
     Fees never count towards it, and mix & match savings are NOT deducted —
     the server checks `serverSubtotalPence`, which is computed before mix &
     match, offers and codes are applied. Deducting them here made the button
     block baskets the server would have accepted. */
  const minShortfallPence = Math.max(0, effectiveMinOrder - subtotal);
  const belowMin = effectiveMinOrder > 0 && minShortfallPence > 0;

  useEffect(() => {
    if (isCourierService && fulfilment !== "delivery") setFulfilment("delivery");
  }, [isCourierService, fulfilment]);

  async function lookupZoneFee(pc?: string, lat?: number, lng?: number): Promise<{ outOfArea: boolean }> {
    // Use postcodeRef (updated synchronously by onChange) so we always read the
    // current input value even when the React render cycle hasn't flushed yet.
    const resolvedPostcode = pc ?? postcodeRef.current;
    if (!resolvedPostcode.trim() && lat === undefined) return { outOfArea: false };
    // Cancel any in-flight request before starting a new one.
    // Do NOT reset feeChecked here — the onChange handler already clears it when
    // the postcode changes.  Resetting here would wipe a valid banner if blur and
    // onClick fire in quick succession (blur completes → feeChecked=true, then
    // onClick startup resets it to false before its own fetch finishes).
    lookupAbortRef.current?.abort();
    const controller = new AbortController();
    lookupAbortRef.current = controller;
    setFeeChecking(true);
    try {
      const r = await fetch(`/api/order/stores/${store.id}/delivery-fee`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customerPostcode: resolvedPostcode.trim() || undefined,
          customerLat: lat,
          customerLng: lng,
        }),
        signal: controller.signal,
      });
      if (!r.ok) {
        // Server error or rate-limited — leave existing banner state unchanged
        // so the user still sees whatever was shown before.
        return { outOfArea: false };
      }
      const d = await r.json() as { feePence: number | null; zoneLabel: string | null; matched: boolean; outOfArea?: boolean };
      if (d.outOfArea) {
        setOutOfArea(true);
        setDeliveryFeeOverride(null);
        setZoneLabel(null);
        setZoneFeeMatched(false);
      } else {
        setOutOfArea(false);
        setDeliveryFeeOverride(d.feePence);
        setZoneLabel(d.zoneLabel);
        setZoneFeeMatched(d.matched);
      }
      lastCheckedPostcode.current = resolvedPostcode.trim();
      setFeeChecked(true);
      return { outOfArea: !!d.outOfArea };
    } catch (err) {
      if ((err as Error)?.name === "AbortError") return { outOfArea: false };
    }
    finally { setFeeChecking(false); }
    return { outOfArea: false };
  }

  async function useMyLocation() {
    if (!navigator.geolocation) return;
    setLocating(true);
    try {
      const pos = await new Promise<GeolocationPosition>((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 10000 })
      );
      const { latitude, longitude } = pos.coords;
      setGeoCoords({ lat: latitude, lng: longitude });
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&addressdetails=1`,
        { headers: { "Accept-Language": "en", "User-Agent": "AyrshireHub/1.0" } }
      );
      const geo = await res.json() as { address?: { road?: string; house_number?: string; suburb?: string; town?: string; city?: string; postcode?: string } };
      const a = geo.address ?? {};
      const street = [a.house_number, a.road].filter(Boolean).join(" ");
      const locality = a.suburb ?? a.town ?? a.city ?? "";
      const fullAddress = [street, locality].filter(Boolean).join(", ");
      if (fullAddress) setAddress(fullAddress);
      if (a.postcode) {
        setPostcode(a.postcode);
        void lookupZoneFee(a.postcode, latitude, longitude);
      } else {
        void lookupZoneFee(undefined, latitude, longitude);
      }
    } catch { /* permission denied or network error */ }
    finally { setLocating(false); }
  }

  async function applyDiscountCode() {
    const code = discountCodeInput.trim();
    if (!code) return;
    setCodeChecking(true);
    setCodeError(null);
    try {
      const authToken = localStorage.getItem("ayrshire_token");
      const r = await fetch(`/api/order/stores/${store.id}/validate-code?code=${encodeURIComponent(code)}&subtotal=${subtotal}`, authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : undefined);
      const d = await r.json() as { valid: boolean; offerTitle?: string; discountPence?: number; error?: string };
      if (d.valid && d.discountPence != null && d.offerTitle) {
        setCodeOffer({ discountPence: d.discountPence, offerTitle: d.offerTitle });
        setAppliedCode(code);
        setDiscountCodeInput("");
        setDiscountCodeOpen(false);
      } else {
        setCodeError(d.error ?? "Invalid code");
        setCodeOffer(null);
        setAppliedCode(null);
      }
    } catch {
      setCodeError("Could not check code. Please try again.");
    } finally {
      setCodeChecking(false);
    }
  }

  if (cart.length === 0) return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <ShoppingCart size={40} className="text-slate-200 mb-3" />
      <p className="text-sm text-slate-400">Your basket is empty</p>
      <p className="text-xs text-slate-300 mt-1">Add items from the menu</p>
    </div>
  );

  return (
    <div data-order-step="checkout" className="food-order-panel flex flex-1 min-h-0 flex-col gap-4">
      <div className="min-w-0 pb-1">
        <p className="text-xs font-semibold text-slate-500 mb-1 truncate">{store.name}</p>
        <h3 className="text-2xl font-bold tracking-tight text-[#1e3a5f]">{inCompanionMode ? "Your basket" : "Checkout"}</h3>
        <p className="text-sm text-slate-500 mt-1">{inCompanionMode ? "Review your items for your combined order." : "Review your items and add your details below."}</p>
      </div>
      <div className="food-panel-scroll space-y-6">
      <FoodSupplierDisclosure storeId={store.id}/>
      <>

      {isCourierService ? (
        <div className="flex gap-3 rounded-xl border border-violet-300 bg-violet-50 p-3">
          <Truck size={20} className="text-primary shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-violet-800">Courier Service</p>
            <p className="text-xs leading-5 text-primary">Collection from your address and return delivery when your item is ready are both included.</p>
          </div>
        </div>
      ) : store.offersDelivery && store.offersCollection && (
        <div className="flex rounded-xl overflow-hidden border border-slate-200">
          {(["delivery", "collection"] as const).map((t) => (
            <button key={t} onClick={() => setFulfilment(t)}
              className={`flex-1 py-2 text-sm font-semibold capitalize transition-colors ${fulfilment === t ? "bg-primary text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              {t}
            </button>
          ))}
        </div>
      )}

      {/* Free item prompt */}
      {freeItemOffer && freeItemQualifies && freeItemsRemaining > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-300 rounded-xl p-3 space-y-2 animate-pulse-once">
          <div className="flex items-center gap-2">
            <Tag size={20} aria-hidden />
            <p className="text-sm font-bold text-amber-900 dark:text-amber-300">
              {freeItemsRemaining === 1
                ? `Pick ${freeItemPickCount === 1 ? "a" : "1 more"} free item!`
                : `Pick ${freeItemsRemaining} free items!`}
            </p>
          </div>
          {freeItemPool.length === 1 ? (
            <button
              onClick={() => onAdd({ cartKey: `free-item-${freeItemPool[0]!.menuItemId}`, menuItemId: freeItemPool[0]!.menuItemId, name: freeItemPool[0]!.name, pricePence: 0, quantity: 1, isFreeItem: true })}
              className="w-full bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-colors"
            >
              Add FREE {freeItemPool[0]!.name}
            </button>
          ) : (
            <div className="space-y-1 max-h-44 overflow-y-auto pr-1">
              {freeItemPool.filter(p => !freeItemsInCart.some(fi => fi.menuItemId === p.menuItemId)).map(p => (
                <button key={p.menuItemId}
                  onClick={() => onAdd({ cartKey: `free-item-${p.menuItemId}`, menuItemId: p.menuItemId, name: p.name, pricePence: 0, quantity: 1, isFreeItem: true })}
                  className="w-full flex items-center justify-between bg-white border border-amber-200 hover:border-amber-400 rounded-lg px-3 py-2 text-xs font-medium text-amber-900 transition-colors"
                >
                  <span>{p.name}</span>
                  <span className="bg-amber-500 text-white px-2 py-0.5 rounded font-bold">FREE</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="space-y-3">
        {cart.map((item) => {
          const photo = menuItems.find(m => m.id === item.menuItemId)?.imageUrl;
          return <article key={item.cartKey} className={`food-basket-line rounded-[22px] p-4 ${item.isFreeItem ? "bg-emerald-50" : "bg-[#f4f6f8]"}`}>
            <div className="flex gap-3">
              {photo && <img src={photo} alt="" className="w-[76px] h-[76px] rounded-2xl object-cover shrink-0" />}
              <div className="flex-1 min-w-0"><div className="flex justify-between gap-2 items-start"><h4 className="text-base font-bold leading-5 tracking-tight text-[#1e3a5f]">{item.name}</h4><span className="text-sm font-bold shrink-0 text-[#1e3a5f]">{item.isFreeItem ? "Free" : pence(calcItemTotal(item))}</span></div>
                {item.optionsSummary && <p className="text-xs text-slate-500 leading-5 mt-1">{item.optionsSummary}</p>}
                {!item.isFreeItem && <p className="text-xs text-slate-500 mt-1">{pence(item.pricePence)} each</p>}
              </div>
            </div>
            <div className="flex items-end justify-between gap-3 mt-3">
              <div className="min-w-0 flex-1">{!item.isFreeItem && <NoteInput value={item.note ?? ""} onChange={(v) => onUpdateNote(item.cartKey, v)} />}</div>
              <div className="flex items-center bg-white rounded-full p-1 shadow-sm shrink-0">
                <button type="button" aria-label={item.quantity === 1 ? `Remove ${item.name} from basket` : `Decrease quantity of ${item.name}`} onClick={() => onRemove(item.cartKey)} className="w-11 h-11 grid place-items-center rounded-full text-[#1e3a5f] hover:bg-slate-100">{item.quantity === 1 ? <Trash2 size={16}/> : <Minus size={16}/>}</button>
                {!item.isFreeItem && <><span aria-live="polite" className="min-w-7 text-center text-sm font-bold tabular-nums">{item.quantity}</span><button type="button" aria-label={`Increase quantity of ${item.name}`} onClick={() => onAdd(item)} className="w-11 h-11 grid place-items-center rounded-full bg-[#1e3a5f] text-white"><Plus size={17}/></button></>}
              </div>
            </div>
          </article>;
        })}
      </div>

      {/* Checkout upsells — "Fancy any of these?" shown right before pay */}
      {visibleUpsells.length > 0 && (
        <div className="pt-1">
          <p className="text-sm font-bold text-slate-800 mb-2">Fancy any of these?</p>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {visibleUpsells.map((u) => (
              <div key={u.menuItemId} className="shrink-0 w-36 rounded-xl border border-amber-200 bg-amber-50 p-2 flex flex-col">
                {u.imageUrl
                  ? <img src={u.imageUrl} alt={u.name} className="w-full h-20 object-cover rounded-lg mb-1.5" />
                  : <div className="w-full h-20 rounded-lg mb-1.5 bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center" aria-hidden="true"><Package size={24} className="text-primary/50" /></div>}
                <p className="text-xs font-semibold text-slate-800 leading-tight" style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{u.name}</p>
                <div className="mt-1 flex items-baseline gap-1">
                  <span className="text-sm font-black text-amber-700">{pence(u.offerPricePence)}</span>
                  {u.savingPence > 0 && <span className="text-[11px] text-slate-400 line-through">{pence(u.normalPricePence)}</span>}
                </div>
                <button
                  onClick={() => onAdd({ cartKey: `upsell-${u.menuItemId}`, menuItemId: u.menuItemId, name: u.name, pricePence: u.offerPricePence, quantity: 1, isUpsell: true })}
                  className="mt-1.5 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold transition-colors"
                >
                  <Plus size={12} /> Add
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      </>
      <div className="rounded-[22px] border border-dashed border-slate-300 bg-white p-5 space-y-3 text-sm">
        {/* Subtotal always shown */}
        <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{pence(serverPricing?.customerSubtotalPence ?? serverPricing?.subtotalPence ?? subtotal)}</span></div>

        {/* Free items (display-only, not pricing lines) */}
        {freeItemsInCart.map((fi) => (
          <div key={fi.cartKey} className="flex justify-between text-green-700 font-semibold">
            <span> {fi.name}</span><span>FREE</span>
          </div>
        ))}

        {/* Delivery label with zone context — display-only wrapper around the server delivery line */}
        {fulfilment === "delivery" && serverPricing == null && (
          <div className="flex justify-between text-slate-600">
            <span>{isCourierService ? "Courier service (collection & return)" : "Delivery"}{zoneLabel ? ` (${zoneLabel})` : ""}</span>
            <span>{fulfilmentFee > 0 ? pence(fulfilmentFee) : <span className="text-emerald-600 font-semibold">Free</span>}</span>
          </div>
        )}

        {/* Server-computed fee lines — rendered directly so new fees appear automatically */}
        {serverPricing?.lines.map((line, idx) => (
          <div key={idx} className={`flex justify-between ${line.type === "discount" ? "text-emerald-600 font-semibold" : line.type === "platform_fee" ? "text-slate-500 text-xs" : "text-slate-600"}`}>
            <span>
              {line.type === "discount" ? " " : ""}
              {line.type === "delivery" && isCourierService ? `Courier service (collection & return)${zoneLabel ? ` (${zoneLabel})` : ""}` : line.label}
              {line.type === "delivery" && !isCourierService && zoneLabel ? ` (${zoneLabel})` : ""}
            </span>
            <span>{line.negative ? "-" : ""}{pence(line.pence)}</span>
          </div>
        ))}

        {/* Local fallback lines shown only while server pricing is loading */}
        {serverPricing == null && (
          <>
            {mixMatchSavings > 0 && (
              <div className="flex justify-between text-teal-600 font-semibold">
                <span> Mix &amp; Match saving</span><span>-{pence(mixMatchSavings)}</span>
              </div>
            )}
            {bogofSavings > 0 && (
              <div className="flex justify-between text-teal-600 font-semibold">
                <span> Deal saving</span><span>-{pence(bogofSavings)}</span>
              </div>
            )}
            {serviceCharge > 0 && <div className="flex justify-between text-slate-600"><span>Shop service charge</span><span>{pence(serviceCharge)}</span></div>}
            {bagCharge > 0 && <div className="flex justify-between text-slate-600"><span>Carrier bag charge</span><span>{pence(bagCharge)}</span></div>}
            {ayrshireHubFee > 0 && (
              <div className="flex justify-between text-slate-500 text-xs"><span>Service fee</span><span>{pence(ayrshireHubFee)}</span></div>
            )}
            {autoOfferDiscountPence > 0 && bestAutoOffer && (
              <div className="flex justify-between text-emerald-600 font-semibold">
                <span> {bestAutoOffer.title}</span><span>-{pence(autoOfferDiscountPence)}</span>
              </div>
            )}
            {codeDiscountPence > 0 && (
              <div className="flex justify-between text-emerald-600 font-semibold">
                <span> {codeOffer!.offerTitle}</span><span>-{pence(codeDiscountPence)}</span>
              </div>
            )}
          </>
        )}

        {/* Random Act of Kindness — display-only line; added to the total server-side */}
        {(kindnessContributionPence ?? 0) > 0 && (
          <div className="flex justify-between items-center text-amber-700 font-semibold">
            <span className="flex items-center gap-1.5">
               Random Act of Kindness contribution
              {onKindnessChange && (
                <button type="button" onClick={onKindnessChange} className="text-xs underline text-amber-600 hover:text-amber-800 font-normal ml-1">change</button>
              )}
            </span>
            <span className="flex items-center gap-1.5">
              +{pence(kindnessContributionPence ?? 0)}
              {onKindnessRemove && (
                <button type="button" onClick={onKindnessRemove} aria-label="Remove kindness contribution" className="text-amber-500 hover:text-amber-700">
                  <X size={13} />
                </button>
              )}
            </span>
          </div>
        )}

        {/* Random Act of Kindness — recipient credit; display-only line, subtracted
            from the shown total. The server reserves/applies the real credit. */}
        {paymentMethod === "card" && (kindnessCreditPreviewPence ?? 0) > 0 && (
          <div className="flex justify-between items-center text-amber-700 font-semibold">
            <span className="flex items-center gap-1.5"> Random Act of Kindness</span>
            <span>−{pence(kindnessCreditPreviewPence ?? 0)}</span>
          </div>
        )}

        <div className="flex justify-between font-bold text-foreground text-base pt-1 border-t border-slate-100">
          <span>Total</span>
          <span>{pence(Math.max(0, (serverPricing?.customerPaidPence ?? total) + (kindnessContributionPence ?? 0) - (paymentMethod === "card" ? (kindnessCreditPreviewPence ?? 0) : 0)))}</span>
        </div>
      </div>

      {/* Discount code */}
      <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
        {appliedCode ? (
          <div className="flex items-center justify-between">
            <span className="text-sm text-emerald-600 font-semibold">Code applied: {appliedCode}</span>
            <button type="button" onClick={() => { setAppliedCode(null); setCodeOffer(null); }} className="text-slate-400 hover:text-slate-600 text-xs underline ml-3">Remove</button>
          </div>
        ) : (
          <>
            <button
              type="button"
              onClick={() => { setDiscountCodeOpen(v => !v); setCodeError(null); }}
              className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"
            >
              <Tag size={13} />
              Got a discount code?
              {discountCodeOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </button>
            {discountCodeOpen && (
              <div className="flex gap-2 mt-2">
                <Input
                  placeholder="Enter code"
                  value={discountCodeInput}
                  onChange={e => { setDiscountCodeInput(e.target.value.toUpperCase()); setCodeError(null); }}
                  className="flex-1 uppercase text-sm"
                  autoComplete="off"
                  onKeyDown={e => { if (e.key === "Enter") void applyDiscountCode(); }}
                />
                <Button
                  type="button"
                  size="sm"
                  disabled={codeChecking || !discountCodeInput.trim()}
                  onClick={() => void applyDiscountCode()}
                >
                  {codeChecking ? "Checking…" : "Apply"}
                </Button>
              </div>
            )}
            {codeError && <p className="text-xs text-red-500 mt-1">{codeError}</p>}
          </>
        )}
      </div>

      {belowMin && <p className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/20 rounded-lg px-3 py-2">Minimum order is {pence(effectiveMinOrder)} of food — add {pence(minShortfallPence)} more. Delivery and service charges don&apos;t count towards it.</p>}

      {!inCompanionMode && <>
      <h3 className="text-xl font-bold tracking-tight text-[#1e3a5f] pt-5 border-t border-slate-100">{fulfilment === "delivery" ? "Delivery & contact" : "Contact details"}</h3>
      {fulfilment === "delivery" && !inCompanionMode && (
        <>
          <div ref={addressFieldRef} tabIndex={-1} role="group" aria-label="Delivery address" aria-invalid={checkoutValidationAttempted && !address.trim()} aria-describedby={checkoutValidationAttempted && !address.trim() ? compactMobileBasket ? "mobile-checkout-address-error" : "checkout-address-error" : undefined} className={`space-y-1.5 ${checkoutValidationAttempted && !address.trim() ? "rounded-xl border border-red-300 p-3" : ""}`}>
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-slate-500">{isCourierService ? "Collection & return address *" : "Delivery address *"}</label>
              <div className="flex items-center gap-3">
                {savedAddresses.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowSavedAddresses((v) => !v)}
                    className="flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-700 transition-colors"
                  >
                    <Clock size={11} />
                    Previous
                    {showSavedAddresses ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => void useMyLocation()}
                  disabled={locating}
                  className="flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary/80 disabled:opacity-50 transition-colors"
                >
                  {locating
                    ? <><Loader2 size={11} className="animate-spin" /> Locating…</>
                    : <><MapPin size={11} /> Use my location</>}
                </button>
              </div>
            </div>
            {showSavedAddresses && savedAddresses.length > 0 && (
              <div className="rounded-lg border border-slate-200 overflow-hidden shadow-sm">
                {savedAddresses.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => {
                      setAddress(s.address);
                      setPostcode(s.postcode);
                      setShowSavedAddresses(false);
                      if (s.postcode) void lookupZoneFee(s.postcode);
                    }}
                    className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 transition-colors ${i < savedAddresses.length - 1 ? "border-b border-slate-100" : ""}`}
                  >
                    <p className="text-sm font-medium text-slate-800 truncate">{s.address || s.postcode}</p>
                    {s.postcode && s.address && <p className="text-xs text-slate-400 mt-0.5">{s.postcode}</p>}
                  </button>
                ))}
              </div>
            )}
            <AddressAutocomplete
              value={address}
              placeholder="e.g. 12 High Street, Ayr"
              onSelect={(addr, pc) => {
                setAddress(addr);
                if (pc) {
                  setPostcode(pc);
                  postcodeRef.current = pc;
                  setFeeChecked(false);
                  setOutOfArea(false);
                  setDeliveryFeeOverride(null);
                  setZoneFeeMatched(false);
                  void lookupZoneFee(pc);
                }
              }}
            />
            {checkoutValidationAttempted && !address.trim() && <p id={compactMobileBasket ? "mobile-checkout-address-error" : "checkout-address-error"} role="alert" className="text-xs text-red-600">Choose an address from the suggestions, or use manual address entry.</p>}
          </div>
          <div className="space-y-1.5">
            <div className="flex gap-2">
              <Input
                placeholder="Postcode"
                value={postcode}
                onChange={(e) => { const v = e.target.value; setPostcode(v); postcodeRef.current = v; setFeeChecked(false); setOutOfArea(false); setDeliveryFeeOverride(null); setZoneFeeMatched(false); }}
                className="flex-1"
                autoComplete="postal-code"
                inputMode="text"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => void lookupZoneFee()}
                disabled={feeChecking || !postcode.trim()}
                className="text-xs shrink-0 min-w-[90px]"
              >
                {feeChecking ? <Loader2 size={12} className="animate-spin" /> : "Check fee"}
              </Button>
            </div>
            {feeChecked && (
              outOfArea
                ? <p className="text-xs rounded-lg px-2.5 py-1.5 font-medium bg-red-50 text-red-600"> Sorry, {isCourierService ? "the courier service is not available" : "we don't deliver"} to this postcode</p>
                : zoneFeeMatched
                  ? <p className={`text-xs rounded-lg px-2.5 py-1.5 font-medium ${deliveryFeeOverride === 0 ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 dark:bg-blue-900/20 text-blue-700"}`}>
                      {deliveryFeeOverride === 0
                        ? ` Free ${isCourierService ? "courier service" : "delivery"} to your area`
                        : ` ${isCourierService ? "Two-way courier fee" : "Delivery fee"} for your area: ${pence(isCourierService ? (deliveryFeeOverride ?? 0) * 2 : (deliveryFeeOverride ?? 0))}${zoneLabel ? ` (${zoneLabel})` : ""}`}
                    </p>
                  : null
            )}
          </div>
        </>
      )}

      {!inCompanionMode && <>
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-slate-500 flex items-center gap-1"><Calendar size={11} /> When do you want it?</label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => { setScheduleMode("asap"); setScheduledFor(""); }}
            className={`flex-1 py-2 rounded-lg text-sm font-bold border-2 transition-colors ${scheduleMode === "asap" ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary/40"}`}
          >
             ASAP
          </button>
          <button
            type="button"
            onClick={() => setScheduleMode("scheduled")}
            className={`flex-1 py-2 rounded-lg text-sm font-bold border-2 transition-colors ${scheduleMode === "scheduled" ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary/40"}`}
          >
             Schedule
          </button>
        </div>
        {scheduleMode === "scheduled" && (
          <WebSchedulePicker
            openingHoursRaw={store.openingHours}
            prepMins={fulfilment === "delivery" ? (store.deliveryTimeMins ?? 30) : (store.collectionTimeMins ?? 20)}
            onChange={setScheduledFor}
          />
        )}
      </div>

      <div className="space-y-1">
        <Input
          ref={nameInputRef}
          aria-invalid={nameTouched && !name.trim()}
          aria-describedby={nameTouched && !name.trim() ? compactMobileBasket ? "mobile-checkout-name-error" : "checkout-name-error" : undefined}
          placeholder="Your name *"
          value={name}
          onChange={(e) => { setName(e.target.value); onNameChange?.(e.target.value); }}
          onBlur={() => { setNameTouched(true); if (name.trim()) onNameChange?.(name.trim()); }}
          className={nameTouched && !name.trim() ? "border-red-300 focus-visible:ring-red-300" : ""}
          autoComplete="name"
        />
        {nameTouched && !name.trim() && <p id={compactMobileBasket ? "mobile-checkout-name-error" : "checkout-name-error"} role="alert" className="text-xs text-red-500 pl-1">Name is required</p>}
      </div>
      {showGuestEmail && (
        <Input
          type="email"
          placeholder="Email address (for order updates)"
          value={guestEmail ?? ""}
          onChange={(e) => onEmailChange?.(e.target.value)}
          onBlur={(e) => onEmailChange?.(e.target.value)}
          autoComplete="email"
        />
      )}
      <div className="space-y-1">
        <Input
          ref={phoneInputRef}
          aria-invalid={phoneTouched && !phone.trim()}
          aria-describedby={phoneTouched && !phone.trim() ? compactMobileBasket ? "mobile-checkout-phone-error" : "checkout-phone-error" : undefined}
          placeholder="Your phone *"
          value={phone}
          onChange={(e) => { setPhone(e.target.value); onPhoneChange?.(e.target.value); }}
          onBlur={() => { setPhoneTouched(true); if (phone.trim()) onPhoneChange?.(phone.trim()); }}
          className={phoneTouched && !phone.trim() ? "border-red-300 focus-visible:ring-red-300" : ""}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
        />
        {phoneTouched && !phone.trim() && <p id={compactMobileBasket ? "mobile-checkout-phone-error" : "checkout-phone-error"} role="alert" className="text-xs text-red-500 pl-1">Phone number is required</p>}
      </div>

      <textarea
        placeholder="Notes for the shop — allergies, special requests… (optional)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={2}
        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-base md:text-sm text-foreground placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
      />

      {fulfilment === "delivery" && store.useHubDrivers && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2">
          <p className="text-xs font-semibold text-amber-800">Want to pledge a tip for your driver? (optional)</p>
          <div className="flex gap-2 flex-wrap">
            {[0, 100, 200, 300, 500].map((amt) => (
              <button key={amt} type="button" onClick={() => setPledgedTipPence(amt === pledgedTipPence ? 0 : amt)}
                className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${pledgedTipPence === amt && amt > 0 ? "bg-amber-400 border-amber-400 text-white" : amt === 0 && pledgedTipPence === 0 ? "bg-amber-200 border-amber-300 text-amber-900" : "bg-white border-amber-200 text-amber-800 hover:bg-amber-100"}`}>
                {amt === 0 ? "None" : `£${(amt / 100).toFixed(0)}`}
              </button>
            ))}
          </div>
          {pledgedTipPence > 0 && <p className="text-xs text-amber-700">You're pledging £{(pledgedTipPence / 100).toFixed(0)} — we'll remind you to send it after delivery.</p>}
        </div>
      )}

      <h3 className="text-xl font-bold tracking-tight text-[#1e3a5f] pt-5 border-t border-slate-100">Payment</h3>
      {store.acceptsCash && !(store.useHubDrivers && fulfilment === "delivery") && (
        <div className="flex rounded-xl overflow-hidden border border-slate-200">
          {(["card", "cash"] as const).map((m) => (
            <button key={m} type="button" onClick={() => setPaymentMethod(m)}
              className={`flex-1 py-2 text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${paymentMethod === m ? "bg-primary text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              {m === "card" ? <><CreditCard size={13} /> Card</> : <><Banknote size={13} /> Cash</>}
            </button>
          ))}
        </div>
      )}

      {paymentMethod === "card" && !store.stripeOnboardingComplete && (
        <p className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/20 rounded-lg px-3 py-2">This shop hasn't connected Stripe yet — card payments unavailable.</p>
      )}
      </>}
      </>}
      </div>
      {/* Minimum-order gate — show a clear message right next to the (disabled)
          checkout button so the customer knows why they can't proceed, instead
          of pressing it and getting a server error. */}
      {belowMin && (
        <p className="text-sm font-semibold text-amber-700 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-lg px-3 py-2">
          Minimum order is {pence(effectiveMinOrder)}. Add {pence(minShortfallPence)} more to check out.
        </p>
      )}
      <div className="sticky bottom-0 z-[2] flex shrink-0 flex-col gap-3 bg-white pt-1">
      {/* Multi-shop action and help use separate, accessible controls. */}
      {!inCompanionMode && onMultiOrderStart && fulfilment === "delivery" && subtotal > 0 && store.useHubDrivers && (
        <>
          <div className="flex items-center rounded-xl border border-amber-200 bg-amber-50/60">
            <button type="button" onClick={onMultiOrderStart} className="flex min-h-11 min-w-0 flex-1 items-center gap-2 px-3 py-2 text-left text-sm font-semibold text-[#1e3a5f]">
              <ShoppingCart size={17} className="shrink-0" /><span className="flex-1">Add groceries or mix &amp; match takeaways</span><Plus size={16} className="shrink-0" />
            </button>
            <button type="button" onClick={() => setShowMultiStoreInfo(true)} aria-label="About multi-shop ordering (Beta)" className="flex h-11 w-11 shrink-0 items-center justify-center text-slate-500"><Info size={17} /></button>
          </div>


          {/* Info modal */}
          {showMultiStoreInfo && (
            <Dialog open onOpenChange={(open) => { if (!open) setShowMultiStoreInfo(false); }}>
              <DialogContent aria-describedby={undefined} className="bg-white rounded-3xl max-w-md max-h-[90dvh] overflow-y-auto p-6 [&>button]:min-h-11 [&>button]:min-w-11 [&>button]:grid [&>button]:place-items-center">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-primary shrink-0"><Package size={20} /></div>
                  <div>
                    <DialogTitle className="font-bold text-slate-800 text-base">Multi-shop ordering</DialogTitle>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 leading-tight tracking-wide">BETA</span>
                      <span className="text-xs text-slate-400">Still in testing</span>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-3">
                  Order from <strong className="text-slate-800">multiple stores in one go</strong> — one driver picks up everything and delivers it all together.
                </p>
                <div className="bg-slate-50 rounded-xl p-4 space-y-2.5 mb-4">
                  {[
                    { icon: "", text: "Add items from this store first" },
                    { icon: "", text: "Click this button to pick a second (or third) store" },
                    { icon: "", text: "One driver collects from all stores" },
                    { icon: "", text: "Everything arrives in a single delivery" },
                  ].map((row, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <span className="text-sm leading-5 shrink-0 text-primary">{i + 1}.</span>
                      <span className="text-sm text-slate-600 leading-5">{row.text}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5">
                  <p className="text-xs font-medium text-amber-800"> This feature is still in testing. It works, but you may occasionally notice rough edges. We'd love your feedback!</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowMultiStoreInfo(false)}
                  className="w-full bg-primary hover:bg-primary/90 text-white font-semibold text-sm py-3 rounded-xl transition-colors"
                >
                  Got it!
                </button>
              </DialogContent>
            </Dialog>
          )}
        </>
      )}

      {/* Companion mode: replace pay button with "Go to combined checkout" */}
      {inCompanionMode ? (
        <Button
          className="food-primary w-full min-h-14 rounded-xl bg-primary hover:bg-primary/90 font-bold"
          style={{ position: "static", boxShadow: "none" }}
          disabled={belowMin || subtotal === 0}
          onClick={() => onMultiOrderCheckout?.()}
        >
          Continue to combined checkout →
        </Button>
      ) : (
        <Button
          className={cn("food-primary w-full min-h-14 rounded-xl bg-primary hover:bg-primary/90 font-bold", closedPayLabel && "whitespace-normal text-center leading-tight py-2")}
          style={{ position: "static", boxShadow: "none" }}
          disabled={loading || (!isTestMode && (store.isOpen === false || checkoutBlockedByTimer)) || belowMin || (fulfilment === "delivery" && outOfArea) || (!isTestMode && paymentMethod === "card" && !store.stripeOnboardingComplete)}
          onClick={async () => {
            setCheckoutValidationAttempted(true);
            setNameTouched(true);
            setPhoneTouched(true);
            if (fulfilment === "delivery" && !address.trim()) { focusMissingCheckoutField("address"); return; }
            if (!name.trim()) { focusMissingCheckoutField("name"); return; }
            if (!phone.trim()) { focusMissingCheckoutField("phone"); return; }
            if (fulfilment === "delivery" && postcode.trim() && postcode.trim() !== lastCheckedPostcode.current) {
              const { outOfArea: isOut } = await lookupZoneFee();
              if (isOut) return;
            }
            if (fulfilment === "delivery") saveAddress(address, postcode);
            onCheckout({ fulfilmentType: fulfilment, deliveryAddress: address, customerPostcode: postcode, scheduledFor, customerName: name, customerPhone: phone, notes, deliveryFeePence: deliveryFee, serviceChargePence: serviceCharge, bagChargePence: bagCharge, totalPence: total, subtotalPence: subtotal, paymentMethod, pledgedTipPence: pledgedTipPence > 0 ? pledgedTipPence : undefined, discountCode: appliedCode ?? undefined });
          }}
        >
          {loading ? "Processing..." : !isTestMode && store.isOpen === false && closedPayLabel ? closedPayLabel : paymentMethod === "cash" ? `Place order – ${pence(total + (kindnessContributionPence ?? 0))} cash` : `Pay ${pence(Math.max(0, total + (kindnessContributionPence ?? 0) - (kindnessCreditPreviewPence ?? 0)))}`}
        </Button>
      )}


      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function OrderStoreDetail({ storeSlug, unifiedPremier = false }: { storeSlug?: string; unifiedPremier?: boolean } = {}) {
  const [, params] = useRoute("/order/:slug");
  const slug = storeSlug ?? params?.slug ?? "";
  const isPremierStore = slug === "premier-dean-street";
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { user, loginWithToken } = useAuth();
  const urlSearch = new URLSearchParams(window.location.search);
  const isTestMode = urlSearch.has("test");
  const isPremierEmbeddedBasket = isPremierStore && urlSearch.get("premierEmbed") === "basket" && window.parent !== window;
  const isPremierEmbeddedProduct = isPremierStore && urlSearch.get("premierEmbed") === "product" && window.parent !== window;
  const isPremierWhiteLabel = isPremierEmbeddedBasket || isPremierEmbeddedProduct;
  const premierItemId = Number(urlSearch.get("premierItemId"));
  // Product links from a store's branded landing page open the existing menu
  // with its normal add-to-basket, modifiers, quote and checkout flow intact.
  const initialProductSearch = urlSearch.get("find")?.trim().slice(0, 120) ?? "";

  // Multi-order companion mode — set when customer arrives from the companion picker
  const multiAnchorStoreId = urlSearch.get("multiAnchorStoreId") ? Number(urlSearch.get("multiAnchorStoreId")) : null;
  const multiAnchorStoreName = urlSearch.get("multiAnchorStoreName") ? decodeURIComponent(urlSearch.get("multiAnchorStoreName")!) : null;
  const anchorSlug = urlSearch.get("anchorSlug") ?? "";
  const companionStoreIdFromUrl = urlSearch.get("companionStoreId") ? Number(urlSearch.get("companionStoreId")) : null;
  const inCompanionMode = !!multiAnchorStoreId;

  const { data, isLoading } = useGetOrderStore(slug,{query:{queryKey:getGetOrderStoreQueryKey(slug),staleTime:0,refetchOnWindowFocus:true,refetchInterval:q=>(q.state.data?.store as unknown as {menuHasDayRestrictions?:boolean}|undefined)?.menuHasDayRestrictions?30_000:false}});
  const { track } = useAnalytics();
  const store = data?.store;
  // The API already hides upsell-only items; hide their navigation categories too.
  const sections = (data?.sections ?? []).filter(section => !(section as typeof section & { upsellOnly?: boolean }).upsellOnly);
  const menuItems = data?.items ?? [];

  const optionGroupsMap = (() => {
    const rawGroups = (data as Record<string, unknown> | undefined)?.optionGroups as { id: number; itemId: number; name: string; required: boolean; multiSelect: boolean; minSelections: number; maxSelections: number | null; dependsOnOptionId: number | null }[] | undefined ?? [];
    const rawChoices = (data as Record<string, unknown> | undefined)?.optionChoices as { id: number; groupId: number; name: string; pricePence: number; available: boolean }[] | undefined ?? [];
    const map: Record<number, OptionGroup[]> = {};
    for (const g of rawGroups) {
      const group: OptionGroup = { ...g, choices: rawChoices.filter((c) => c.groupId === g.id) };
      if (!map[g.itemId]) map[g.itemId] = [];
      map[g.itemId].push(group);
    }
    return map;
  })();

  const [cart, setCart] = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem(`order_cart_${slug}`);
      if (!saved) return [];
      return (JSON.parse(saved) as CartItem[]).map(i => ({
        ...i,
        quantity: Math.max(1, Number.isFinite(i.quantity) ? i.quantity : 1),
        pricePence: Number.isFinite(i.pricePence) ? i.pricePence : 0,
      })).filter(i => i.cartKey && i.menuItemId);
    } catch { return []; }
  });
  // Basket-emptied bookkeeping — `cartHadItemsRef` makes an empty cart mean
  // "emptied" rather than "never started", so browsing a store never wipes a
  // basket saved on another device.
  const cartHadItemsRef = useRef(cart.length > 0);
  const cartEmptiedRef = useRef(false);
  const isShopLocal = isShopLocalStore(store);
  /**
   * Sub-sections are no longer retail-only.
   *
   * The drill-down was gated to Shop Local because groceries were the use case
   * that prompted it, but the shape is just as right for a takeaway — "Pizzas"
   * splitting into 10 inch and 12 inch, say. The gate meant a chippy could
   * build a nested menu in the editor and then watch it render flat, which is
   * a worse outcome than not offering it.
   *
   * Safe to widen: it still only applies to stores that have deliberately
   * turned it on, and no live store had before this shipped.
   */
  const subcategoriesEnabled = !!(store as unknown as Record<string, unknown> | null)?.subcategoriesEnabled;
  const parentSections = (subcategoriesEnabled ? sections.filter(s => !s.parentSectionId) : sections)
    .filter(s => !isShopLocal || menuItems.some(i => i.sectionId === s.id || sections.some(child => child.id === i.sectionId && child.parentSectionId === s.id)));
  const getChildSections = (parentId: number) => sections.filter(s => s.parentSectionId === parentId && menuItems.some(i => i.sectionId === s.id));

  /**
   * Grocery catalogue vs food menu — derived, exactly as the app derives it
   * (mobile/app/order-store.tsx:1362), because there is no `storeType` column.
   * Drives DENSITY only: which MenuItemCard layout to use and whether to drop
   * per-item descriptions. It never touches pricing, availability or the cart.
   *
   * `isShopLocal` (a category allow-list) stays the gate for the shop-local
   * navigation model — category tiles, one-section-at-a-time rendering — which
   * is a separate concern from how an individual product is drawn.
   */
  const catalogueMode = useMemo(
    // `isShopLocal &&` matters now that any store can nest sections: the flag
    // used to imply "this is a grocery", and passing it bare would drop a
    // takeaway into the dense, description-light grocery layout the moment it
    // split its pizzas by size. Nesting and density are separate decisions.
    () => deriveCatalogueMode(menuItems, isShopLocal && subcategoriesEnabled),
    [menuItems, isShopLocal, subcategoriesEnabled],
  );

  /**
   * Store rating. The single-store endpoint does not carry avgRating, so this
   * is the same separate approved-reviews fetch the app makes
   * (mobile/app/order-store.tsx:1326). Renders nothing until the shop has
   * approved reviews — no placeholder, no zero-star row.
   */
  const reviewParams = useMemo(
    () => ({ targetType: "order_store" as const, targetId: store?.id ?? 0, approved: true }),
    [store?.id],
  );
  const { data: reviewsData } = useGetReviews(reviewParams, {
    query: { queryKey: getGetReviewsQueryKey(reviewParams), enabled: !!store?.id, staleTime: 5 * 60 * 1000 },
  });
  const storeRating = useMemo(() => {
    const rows = reviewsData?.items ?? [];
    if (rows.length === 0) return null;
    const sum = rows.reduce((acc, r) => acc + (r.rating ?? 0), 0);
    return { avg: sum / rows.length, count: rows.length };
  }, [reviewsData]);
  const [viewMode, setViewMode] = useState<"grid" | "list">(() => {
    try {
      const saved = localStorage.getItem("shop_local_view_mode");
      if (saved === "grid" || saved === "list") return saved;
    } catch { /* ignore */ }
    return "list";
  });
  /**
   * With no saved preference, pick the default the shop deserves — a grocery
   * catalogue reads best as a grid, a food menu as product cards. Mirrors the
   * app (mobile/app/order-store.tsx:1423). An explicit choice always wins, so
   * this only ever runs once and only when the user has never chosen.
   */
  const viewModeInitialised = useRef(false);
  useEffect(() => {
    if (viewModeInitialised.current || !store) return;
    viewModeInitialised.current = true;
    try { if (localStorage.getItem("shop_local_view_mode")) return; } catch { /* ignore */ }
    setViewMode(catalogueMode ? "grid" : "list");
  }, [store, catalogueMode]);
  // For shop local: only render one section at a time to keep 5000-product stores fast.
  const [sectionFilter, setSectionFilter] = useState<number | null>(null);
  // When subcategoriesEnabled: tracks which child section's items to show (null = show subcategory tiles).
  const [subcategoryFilter, setSubcategoryFilter] = useState<number | null>(null);
  useEffect(() => { setSectionFilter(null); setSubcategoryFilter(null); }, [store?.id]);
  useEffect(() => {
    if (!unifiedPremier || !store || !sections.length) return;
    const initial = sections.find(s => s.name === "BREAD & CAKES" && !s.parentSectionId) ?? sections.find(s => !s.parentSectionId);
    if (initial) setSectionFilter(current => current ?? initial.id);
  }, [unifiedPremier, store?.id, data?.sections]);
  const [premierVisibleCount, setPremierVisibleCount] = useState(24);
  const [cartOpen, setCartOpen] = useState(urlSearch.has("basket"));
  const embeddedCheckoutStartedRef = useRef(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [guestContact, setGuestContact] = useState({ name: "", phone: "", postcode: "", email: "" });
  const [recoveryConsent, setRecoveryConsent] = useState(false);
  const [initialDiscountCode, setInitialDiscountCode] = useState<string | undefined>(undefined);
  const [menuSearch, setMenuSearch] = useState(initialProductSearch);
  const [menuSearchInput, setMenuSearchInput] = useState(initialProductSearch);
  const [showPremierFilters, setShowPremierFilters] = useState(false);
  const [groceryFilters, setGroceryFilters] = useState(emptyGroceryFilters);
  useEffect(() => { setGroceryFilters(emptyGroceryFilters); }, [store?.id, sectionFilter, subcategoryFilter]);
  const groceryFiltersActive = isShopLocal && Object.values(groceryFilters).some(Boolean);
  const groceryScope = useMemo(() => {
    const query = menuSearch.trim().toLowerCase();
    const sectionNames = new Map(sections.map(s => [s.id, s.name.toLowerCase()]));
    const categoryIds = new Set(subcategoryFilter !== null ? [subcategoryFilter] : sectionFilter !== null ? [sectionFilter, ...sections.filter(s => s.parentSectionId === sectionFilter).map(s => s.id)] : []);
    return menuItems.filter(item => item.available !== false && (query
      ? item.name.toLowerCase().includes(query) || (item.description ?? "").toLowerCase().includes(query) || (sectionNames.get(item.sectionId) ?? "").includes(query)
      : categoryIds.size === 0 || categoryIds.has(item.sectionId)));
  }, [menuItems, sections, menuSearch, sectionFilter, subcategoryFilter]);
  const groceryOptions = useMemo(() => groceryFilterOptions(groceryScope, [...new Set(groceryScope.map(item => {
    const section = sections.find(s => s.id === item.sectionId);
    const parent = sections.find(s => s.id === section?.parentSectionId);
    return [parent?.name, section?.name].filter(Boolean).join(" ");
  }))]), [groceryScope, sections]);
  const groceryResults = useMemo(() => groceryScope.filter(item => matchesGroceryFilters(item.name, groceryFilters)), [groceryScope, groceryFilters]);

  // Debounce search to avoid filtering 5000 items on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setMenuSearch(menuSearchInput), 250);
    return () => clearTimeout(t);
  }, [menuSearchInput]);
  const [collapsedSections, setCollapsedSections] = useState<Set<number>>(new Set());
  const [pickerItem, setPickerItem] = useState<{ id: number; name: string; imageUrl?: string | null; basePrice: number; isEndToEnd?: boolean } | null>(null);
  const openedEmbeddedProductRef = useRef(false);
  useEffect(() => {
    if (!isPremierEmbeddedProduct || openedEmbeddedProductRef.current || !Number.isSafeInteger(premierItemId) || premierItemId <= 0) return;
    const item = menuItems.find(candidate => candidate.id === premierItemId);
    if (!item) return;
    openedEmbeddedProductRef.current = true;
    if ((optionGroupsMap[item.id]?.length ?? 0) > 0) setPickerItem({ id: item.id, name: item.name, imageUrl: item.imageUrl, basePrice: item.pricePence });
  }, [isPremierEmbeddedProduct, premierItemId, menuItems.length]);
  /* Stable across renders. As an inline arrow this changed identity on every
     tick of the closing countdown, churning any effect in the modal that
     depends on it. */
  const closePicker = useCallback(() => setPickerItem(null), []);
  const [activeSection, setActiveSection] = useState<number | null>(null);
  const [showMenuCategories, setShowMenuCategories] = useState(false);
  const [showStoreInfo, setShowStoreInfo] = useState(false);
  const [showAllergyInfo, setShowAllergyInfo] = useState(false);
  const [mixMatchDeals, setMixMatchDeals] = useState<MixMatchDeal[]>([]);
  const cashIdempotencyKeyRef = useRef(crypto.randomUUID());

  // ── Random Act of Kindness ────────────────────────────────────────────────
  // Feature config (null when disabled / unknown — then nothing below runs and
  // behaviour is identical to before the feature existed).
  const kindnessConfig = useKindnessConfig();
  // Chosen contribution for THIS checkout. Persisted so the summary line and the
  // request stay in sync; cleared after a successful redirect / order.
  const [kindnessContributionPence, setKindnessContributionPence] = useState(0);
  const [kindnessDisplay, setKindnessDisplay] = useState<KindnessDisplay>("anonymous");
  // Modal state + the pending checkout opts captured when the user taps pay.
  const [kindnessModalOpen, setKindnessModalOpen] = useState(false);
  const pendingCheckoutOptsRef = useRef<Parameters<typeof handleCheckout>[0] | null>(null);
  // Prompt only ONCE per checkout attempt — never re-prompt on a retry.
  const kindnessPromptedRef = useRef(false);
  // One idempotency key per checkout attempt; survives Stripe-redirect retries.
  const kindnessIdempotencyKeyRef = useRef<string>(
    (() => {
      try {
        const existing = sessionStorage.getItem("kindness_idempotency_key");
        if (existing) return existing;
        const fresh = crypto.randomUUID();
        sessionStorage.setItem("kindness_idempotency_key", fresh);
        return fresh;
      } catch { return crypto.randomUUID(); }
    })(),
  );

  /**
   * Checkout entry point used by CartPanel. When the kindness feature is enabled
   * and we have not yet prompted this attempt, show the branded modal first and
   * defer the real checkout until it resolves. Otherwise go straight through —
   * so with the feature disabled this is a transparent pass-through.
   */
  function startCheckout(opts: Parameters<typeof handleCheckout>[0]) {
    if (kindnessConfig && !kindnessPromptedRef.current) {
      pendingCheckoutOptsRef.current = opts;
      kindnessPromptedRef.current = true;
      setKindnessModalOpen(true);
      return;
    }
    void handleCheckout({ ...opts, kindnessContributionPence, kindnessDisplay });
  }

  function handleKindnessResolve(result: { contributionPence: number; display: KindnessDisplay }) {
    setKindnessModalOpen(false);
    setKindnessContributionPence(result.contributionPence);
    setKindnessDisplay(result.display);
    const opts = pendingCheckoutOptsRef.current;
    pendingCheckoutOptsRef.current = null;
    if (opts) {
      void handleCheckout({ ...opts, kindnessContributionPence: result.contributionPence, kindnessDisplay: result.display });
    }
  }
  // ── Random Act of Kindness — RECIPIENT side (page-level) ──────────────────
  // A previous donor may have paid forward towards THIS customer's order. We ask
  // the server ONCE per store visit (not per CartPanel instance — CartPanel
  // mounts in both the desktop sidebar and the mobile sheet) how much credit
  // applies to the current food total, then pop a thank-you modal while the
  // customer is BROWSING to encourage them to order. The credit amount is passed
  // down to CartPanel so its "−£X Random Act of Kindness" line and reduced Total
  // keep working at checkout. Display-only; the server reserves/applies the real
  // credit. On failure or zero credit, behaviour is identical to before.
  const [kindnessCreditPreviewPence, setKindnessCreditPreviewPence] = useState(0);
  const [kindnessDonorName, setKindnessDonorName] = useState<string | null>(null);
  const [kindnessRecipientModalOpen, setKindnessRecipientModalOpen] = useState(false);
  // Fires exactly once per store visit, even though CartPanel mounts twice.
  const kindnessRecipientFetchedRef = useRef(false);
  const kindnessFoodPence = cart.filter(i => !i.isFreeItem).reduce((s, i) => s + calcItemTotal(i), 0);
  useEffect(() => {
    if (!kindnessConfig) return;
    if (!user) return;
    // Card is the default/available payment method unless the shop forces cash.
    if (kindnessRecipientFetchedRef.current) return;
    if (kindnessFoodPence <= 0) return;
    kindnessRecipientFetchedRef.current = true;
    let cancelled = false;
    void (async () => {
      try {
        const token = localStorage.getItem("ayrshire_token");
        const res = await fetch(
          `/api/kindness/available?foodPence=${kindnessFoodPence}`,
          token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
        );
        if (!res.ok) return;
        const dataRes = (await res.json()) as { creditPence?: number; donorName?: string | null };
        const credit = typeof dataRes.creditPence === "number" ? Math.max(0, Math.round(dataRes.creditPence)) : 0;
        if (cancelled || credit <= 0) return;
        setKindnessCreditPreviewPence(credit);
        setKindnessDonorName(dataRes.donorName ?? null);
        setKindnessRecipientModalOpen(true);
      } catch { /* feature stays invisible on error */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kindnessConfig, user, kindnessFoodPence]);

  const secsLeft = useClosingCountdown((store as { closesAtTimestamp?: string | null } | null)?.closesAtTimestamp);
  const showTimer = secsLeft !== null && secsLeft <= CLOSING_WARN_SECS;
  const checkoutBlockedByTimer = secsLeft !== null && secsLeft < CHECKOUT_MIN_SECS;

  useEffect(() => {
    if (sections.length > 0 && activeSection === null) setActiveSection(sections[0].id);
  }, [sections]);

  // Detect ?recover=CODE in the URL — restore basket and pre-apply offer code
  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("recover");
    if (!code) return;
    void (async () => {
      try {
        const r = await fetch(`/api/order/recover/${encodeURIComponent(code)}`);
        if (!r.ok) return;
        const d = await r.json() as {
          storeId: number;
          basketItems: CartItem[];
          estimatedSubtotalPence: number;
          fulfilmentType: string | null;
          offer: { code: string; title: string; type: string; value: number } | null;
        };
        if (Array.isArray(d.basketItems) && d.basketItems.length > 0) {
          setCart((prev) => {
            if (prev.length > 0) return prev; // don't overwrite an active basket
            return d.basketItems.filter((i) => i.cartKey && i.menuItemId);
          });
        }
        if (d.offer?.code) {
          setInitialDiscountCode(d.offer.code);
        }
        // Remove the ?recover= param from the URL without reloading
        const url = new URL(window.location.href);
        url.searchParams.delete("recover");
        window.history.replaceState({}, "", url.toString());
      } catch { /* non-critical */ }
    })();
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(`order_cart_${slug}`, JSON.stringify(cart));
      // Remember that the customer emptied the basket, so a stale server copy is
      // never pulled back in on the next visit even if the sync below failed.
      if (cart.length > 0) {
        cartHadItemsRef.current = true;
        cartEmptiedRef.current = false;
        localStorage.removeItem(`order_cart_emptied_${slug}`);
      } else if (cartHadItemsRef.current) {
        cartEmptiedRef.current = true;
        localStorage.setItem(`order_cart_emptied_${slug}`, String(Date.now()));
      }
    } catch {}
  }, [cart]);

  // Auto-save basket to server (creates a guest account if needed) whenever cart
  // changes. An emptied basket is synced too — otherwise the last non-empty
  // snapshot lives on server-side and keeps triggering recovery messages.
  useEffect(() => {
    if (!store?.id) return;
    if (!cart.length) {
      // Emptying is a single deliberate action — send it straight away. A
      // debounce would be cancelled by navigating away, which is exactly when
      // the server copy must already be empty.
      if (cartHadItemsRef.current) saveBasketToServer([]);
      return;
    }
    const t = setTimeout(() => saveBasketToServer(cart), 1500);
    return () => clearTimeout(t);
  }, [cart, store?.id]);

  // Lock body scroll when cart modal is open
  useEffect(() => {
    if (cartOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [cartOpen]);

  useEffect(() => {
    if (store) track("store_viewed", { screen: "order-store", metadata: { storeName: store.name, storeId: store.id } });
  }, [store?.id]);

  useEffect(() => {
    if (!store) return;
    try {
      const KEY = "ah_recently_viewed_stores";
      const raw = localStorage.getItem(KEY);
      const prev: { id: number; name: string; slug: string; imageUrl?: string | null }[] = raw ? JSON.parse(raw) as typeof prev : [];
      const next = [
        { id: store.id, name: store.name, slug: String(slug), imageUrl: store.imageUrl ?? null },
        ...prev.filter(s => s.id !== store.id),
      ].slice(0, 10);
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch {}
  }, [store?.id]);

  useEffect(() => {
    if (!menuItems.length) return;
    const menuMap = new Map(menuItems.map((item) => [item.id, item]));
    setCart((prev) => prev.map((item) => {
      const menuItem = menuMap.get(item.menuItemId) as { isEndToEnd?: boolean | null } | undefined;
      return menuItem ? { ...item, isEndToEnd: menuItem.isEndToEnd === true } : item;
    }));
  }, [menuItems]);

  useEffect(() => {
    if (!menuItems.length) return;
    const isReorder = (() => { try { return !!localStorage.getItem(`order_cart_reorder_flag_${slug}`); } catch { return false; } })();
    if (!isReorder) return;
    try { localStorage.removeItem(`order_cart_reorder_flag_${slug}`); } catch {}
    const menuMap = new Map(menuItems.map((m) => [m.id, m]));
    setCart((prev) => {
      const valid: CartItem[] = [];
      let skipped = 0;
      let priceChanged = false;
      for (const c of prev) {
        const menuItem = menuMap.get(c.menuItemId);
        if (!menuItem || !menuItem.available) {
          skipped++;
          continue;
        }
        const currentPrice = menuItem.pricePence;
        if (currentPrice !== c.pricePence) priceChanged = true;
        valid.push({ ...c, pricePence: currentPrice });
      }
      if (skipped > 0) {
        toast({
          title: `${skipped} item${skipped > 1 ? "s are" : " is"} no longer available and ${skipped > 1 ? "were" : "was"} removed from your basket`,
        });
      }
      if (priceChanged) {
        toast({
          title: "Prices may have changed since your last order",
        });
      }
      return valid;
    });
  }, [menuItems]);

  useEffect(() => {
    if (!store?.id) return;
    fetch(`/api/order/stores/${store.id}/mix-match`)
      .then((r) => r.json())
      .then((d: { items: MixMatchDeal[] }) => setMixMatchDeals(d.items ?? []))
      .catch(() => {});
  }, [store?.id]);

  // Restore basket from server if the local basket is empty (cross-device sync).
  // Skipped entirely once the customer has emptied the basket here — that is a
  // deliberate act, not an abandoned basket to be handed back.
  useEffect(() => {
    if (!store?.id || !user) return;
    const local = (() => {
      try {
        const raw = localStorage.getItem(`order_cart_${slug}`);
        // The emptied marker is a 24h fallback for a sync that never reached the
        // server. Beyond that the server copy is authoritative again, so a basket
        // genuinely left on another device can still be offered back.
        const emptiedAt = Number(localStorage.getItem(`order_cart_emptied_${slug}`) ?? 0);
        const emptied = emptiedAt > 0 && Date.now() - emptiedAt < 24 * 60 * 60 * 1000;
        const parsed = raw ? JSON.parse(raw) as unknown : null;
        return { hasItems: Array.isArray(parsed) && parsed.length > 0, emptied };
      } catch { return { hasItems: false, emptied: false }; }
    })();
    if (local.hasItems || local.emptied || cartEmptiedRef.current) return;
    const token = localStorage.getItem("ayrshire_token");
    if (!token) return;
    fetch(`/api/order/basket/${store.id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d: { basket?: { items: CartItem[] } | null }) => {
        if (cartEmptiedRef.current) return;
        const items = (d.basket?.items ?? [])
          .filter(i => i.cartKey && i.menuItemId)
          .map(i => ({
            ...i,
            quantity: Math.max(1, Number.isFinite(i.quantity) ? i.quantity : 1),
            pricePence: Number.isFinite(i.pricePence) ? i.pricePence : 0,
          }));
        if (!items.length) return;
        setCart(prev => prev.length > 0 ? prev : items);
      })
      .catch(() => {});
  }, [store?.id, user]);

  async function ensureGuestSession(): Promise<string | null> {
    const existing = localStorage.getItem("ayrshire_token");
    if (existing) return existing;
    try {
      const r = await fetch("/api/auth/guest", { method: "POST", headers: { "Content-Type": "application/json" } });
      if (!r.ok) return null;
      const d = await r.json() as { token: string };
      await loginWithToken(d.token);
      return d.token;
    } catch { return null; }
  }

  function saveBasketToServer(currentCart: CartItem[]) {
    if (!store?.id) return;
    const doSave = async () => {
      // Never mint a guest account just to record an empty basket.
      const token = currentCart.length ? await ensureGuestSession() : localStorage.getItem("ayrshire_token");
      if (!token) return;
      const subtotal = currentCart.reduce((s, i) => s + i.pricePence * i.quantity, 0);
      fetch("/api/order/basket", {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ storeId: store.id, items: currentCart, estimatedSubtotalPence: subtotal }),
      }).catch(() => {});
    };
    void doSave();
  }

  function saveGuestContact(patch: { name?: string; phone?: string; postcode?: string; email?: string; recoveryConsent?: boolean }) {
    if (!store?.id) return;
    const hasString = Object.entries(patch).some(([k, v]) => k !== "recoveryConsent" && typeof v === "string" && v.trim());
    const hasConsent = patch.recoveryConsent !== undefined;
    if (!hasString && !hasConsent) return;
    const doSave = async () => {
      const token = await ensureGuestSession();
      if (!token) return;
      fetch("/api/order/basket-contact", {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ storeId: store!.id, ...patch }),
      }).catch(() => {});
    };
    void doSave();
  }

  function openCart() {
    saveBasketToServer(cart);
    setCartOpen(true);
  }

  function scrollToSection(sectionId: number) {
    if (isShopLocal) {
      const section = sections.find(s => s.id === sectionId);
      if (!section) return;
      setSectionFilter(section.parentSectionId ?? section.id);
      setSubcategoryFilter(unifiedPremier ? null : section.parentSectionId ? section.id : null);
      if (unifiedPremier) setPremierVisibleCount(24);
      setActiveSection(section.parentSectionId ?? section.id);
      return;
    }
    setActiveSection(sectionId);
    const el = document.getElementById(`section-${sectionId}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const cartTotal = cart.reduce((s, i) => s + calcItemTotal(i), 0) - calcMixMatchSavings(cart, mixMatchDeals) - calcBogofSavings(cart, (store?.activeOffers as ActiveOffer[]) ?? [], menuItems as Array<{ id: number; sectionId: number }>);

  function buildCartKey(menuItemId: number, selections: SelectedOption[]) {
    const ids = selections.map((s) => s.optionId).sort().join(",");
    return ids ? `${menuItemId}_${ids}` : `${menuItemId}_default`;
  }

  function addWithOptions(item: { id: number; name: string; basePrice: number; multiBuyQty?: number | null; multiBuyPricePence?: number | null; isEndToEnd?: boolean }, selections: SelectedOption[], note?: string) {
    const effectivePrice = item.basePrice + selections.reduce((s, o) => s + o.pricePence, 0);
    const cartKey = buildCartKey(item.id, selections);
    const optionsSummary = selections.length > 0
      ? (() => {
          const counts = new Map<number, { name: string; qty: number }>();
          for (const s of selections) {
            const e = counts.get(s.optionId);
            if (e) e.qty++; else counts.set(s.optionId, { name: s.optionName, qty: 1 });
          }
          return Array.from(counts.values()).map((e) => e.qty > 1 ? `${e.qty}× ${e.name}` : e.name).join(", ");
        })()
      : undefined;
    const multiBuyQty = selections.length === 0 ? item.multiBuyQty : null;
    const multiBuyPricePence = selections.length === 0 ? item.multiBuyPricePence : null;
    setCart((prev) => {
      const existing = prev.find((c) => c.cartKey === cartKey);
      if (existing) return prev.map((c) => c.cartKey === cartKey ? { ...c, quantity: c.quantity + 1 } : c);
      return [...prev, { cartKey, menuItemId: item.id, name: item.name, pricePence: effectivePrice, quantity: 1, note: note || undefined, selectedOptions: selections.length > 0 ? selections : undefined, optionsSummary, multiBuyQty, multiBuyPricePence, isEndToEnd: item.isEndToEnd }];
    });
    setPickerItem(null);
  }

  function handleAddClick(item: { id: number; name: string; pricePence: number; imageUrl?: string | null; multiBuyQty?: number | null; multiBuyPricePence?: number | null; isEndToEnd?: boolean }) {
    const groups = optionGroupsMap[item.id] ?? [];
    if (groups.length > 0) {
      setPickerItem({ id: item.id, name: item.name, imageUrl: item.imageUrl, basePrice: item.pricePence, isEndToEnd: item.isEndToEnd });
    } else {
      addWithOptions({ id: item.id, name: item.name, basePrice: item.pricePence, multiBuyQty: item.multiBuyQty, multiBuyPricePence: item.multiBuyPricePence, isEndToEnd: item.isEndToEnd }, []);
    }
  }

  function incrementCartItem(cartKey: string) {
    setCart((prev) => prev.map((c) => c.cartKey === cartKey ? { ...c, quantity: c.quantity + 1 } : c));
  }

  function removeFromCart(cartKey: string) {
    setCart((prev) => {
      const existing = prev.find((c) => c.cartKey === cartKey);
      if (!existing) return prev;
      if (existing.quantity <= 1) return prev.filter((c) => c.cartKey !== cartKey);
      return prev.map((c) => c.cartKey === cartKey ? { ...c, quantity: c.quantity - 1 } : c);
    });
  }

  function addToCart(item: CartItem) {
    setCart((prev) => {
      if (prev.find((c) => c.cartKey === item.cartKey)) return prev;
      return [...prev, item];
    });
  }

  // Single entry point for the cart "+" and the upsell "Add". Free items and
  // checkout-upsell items are added verbatim (preserving isFreeItem / isUpsell and
  // their offer price); everything else flows through the normal options path.
  // Incrementing an existing upsell line bumps quantity while keeping the flag.
  function handleCartAdd(item: CartItem) {
    if (item.isFreeItem) { addToCart(item); return; }
    if (item.isUpsell) {
      setCart((prev) => {
        const existing = prev.find((c) => c.cartKey === item.cartKey);
        if (existing) return prev.map((c) => c.cartKey === item.cartKey ? { ...c, quantity: c.quantity + 1 } : c);
        return [...prev, { ...item, quantity: 1 }];
      });
      return;
    }
    addWithOptions({ id: item.menuItemId, name: item.name, basePrice: item.pricePence }, item.selectedOptions ?? []);
  }

  function cartQty(itemId: number) {
    return cart.filter((c) => c.menuItemId === itemId).reduce((s, c) => s + c.quantity, 0);
  }

  function toggleSection(id: number) {
    setCollapsedSections((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  async function handleCheckout(opts: { fulfilmentType: string; deliveryAddress: string; customerPostcode: string; scheduledFor: string; customerName: string; customerPhone: string; notes: string; deliveryFeePence: number; serviceChargePence: number; bagChargePence: number; totalPence: number; subtotalPence: number; paymentMethod: "card" | "cash"; pledgedTipPence?: number; discountCode?: string; kindnessContributionPence?: number; kindnessDisplay?: KindnessDisplay }) {
    if (isTestMode) {
      toast({ title: "Test mode", description: "This is a preview — no order has been placed." });
      return;
    }
    if (!store) return;
    setCheckingOut(true);
    const { paymentMethod: pm, customerPostcode, deliveryAddress: rawAddress, discountCode, kindnessContributionPence: kindPence, kindnessDisplay: kindDisplay, ...restOpts } = opts;
    // Ensure postcode is always part of the stored delivery address
    const pc = customerPostcode?.trim() ?? "";
    const a  = rawAddress?.trim() ?? "";
    const deliveryAddress = a
      ? (pc && !a.toLowerCase().includes(pc.toLowerCase()) ? `${a}, ${pc}` : a)
      : pc || undefined;
    let checkoutNavigating = false;
    try {
      let token = localStorage.getItem("ayrshire_token");
      if (!token) {
        try {
          const guestRes = await fetch("/api/auth/guest", { method: "POST", headers: { "Content-Type": "application/json" } });
          if (!guestRes.ok) throw new Error("guest create failed");
          const guestData = await guestRes.json() as { token: string };
          token = guestData.token;
          localStorage.setItem("ayrshire_token", token);
          localStorage.setItem("ayrshire_is_guest", "1");
          void loginWithToken(token);
        } catch {
          toast({ title: "Could not start checkout", description: "Please check your connection and try again.", variant: "destructive" });
          setCheckingOut(false);
          return;
        }
      }
      const headers = { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      const orderItems = cart.map((i) => ({ menuItemId: i.menuItemId, name: i.name, pricePence: i.isFreeItem ? 0 : i.pricePence, quantity: i.quantity, note: i.note || undefined, selectedOptions: i.selectedOptions, isFreeItem: i.isFreeItem ?? false, isUpsell: i.isUpsell ?? false }));
      if (pm === "cash") {
        const cashIdempotencyKey = cashIdempotencyKeyRef.current;
        const res = await fetch("/api/order/cash-order", {
          method: "POST", headers,
          body: JSON.stringify({
            storeId: store.id, items: orderItems, ...restOpts, deliveryAddress, customerPostcode: pc || undefined, cashIdempotencyKey, discountCode: discountCode || undefined,
            ...(kindnessConfig ? {
              kindnessContributionPence: kindPence ?? 0,
              kindnessDisplay: kindDisplay ?? "anonymous",
              kindnessReceive: true,
              expectedKindnessCreditPence: kindnessCreditPreviewPence,
              kindnessIdempotencyKey: kindnessIdempotencyKeyRef.current,
            } : {}),
          }),
        });
        const d = await res.json() as { order?: { id: number }; error?: string };
        if (d.order) {
          track("order_placed", { screen: "order-store", metadata: { storeName: store.name, paymentMethod: "cash", totalPence: opts.totalPence } });
          try { localStorage.removeItem(`order_cart_${slug}`); sessionStorage.removeItem("kindness_idempotency_key"); } catch {}
          checkoutNavigating = true;
          navigate(`/order/success?storeId=${store.id}&cash=1&orderId=${d.order.id}`);
        } else {
          toast({ title: d.error ?? "Order failed", variant: "destructive", action: <ToastAction altText="Try again" onClick={() => void handleCheckout(opts)}>Try again</ToastAction> });
        }
      } else {
        const base = window.location.origin;
        const res = await fetch("/api/order/checkout-session", {
          method: "POST", headers,
          body: await withCheckoutAttempt({
            storeId: store.id, items: orderItems, ...restOpts,
            deliveryAddress, customerPostcode: pc || undefined,
            discountCode: discountCode || undefined,
            ...(kindnessConfig ? {
              kindnessContributionPence: kindPence ?? 0,
              kindnessDisplay: kindDisplay ?? "anonymous",
              kindnessReceive: true,
              expectedKindnessCreditPence: kindnessCreditPreviewPence,
              kindnessIdempotencyKey: kindnessIdempotencyKeyRef.current,
            } : {}),
            successUrl: `${base}/order/success?storeId=${store.id}&session_id={CHECKOUT_SESSION_ID}`,
            cancelUrl: isPremierEmbeddedBasket ? `${base}/premier/?basket=1` : `${base}/order/${slug}`,
          },checkoutAttemptStorageKey(store.id,"hosted"),sessionStorage,()=>crypto.randomUUID()),
        });
        const d = await res.json() as { url?: string; free?: boolean; order?: { id: number }; error?: string; pending?: boolean; newAttemptAllowed?:boolean;paid?:boolean;sessionId?:string };
        if(d.newAttemptAllowed)sessionStorage.removeItem(checkoutAttemptStorageKey(store.id,"hosted"));
        if (d.free && d.order) {
          sessionStorage.removeItem(checkoutAttemptStorageKey(store.id,"hosted"));
          // Zero-payment order fully covered by the kindness fund — no Stripe redirect.
          track("checkout_started", { screen: "order-store", metadata: { storeName: store.name, paymentMethod: "kindness_free", totalPence: 0 } });
          try { localStorage.removeItem(`order_cart_${slug}`); sessionStorage.removeItem("kindness_idempotency_key"); } catch {}
          checkoutNavigating = true;
          navigate(`/order/success?storeId=${store.id}&free=1&orderId=${d.order.id}`);
        } else if(d.paid&&d.sessionId){checkoutNavigating = true; navigate(`/order/success?storeId=${store.id}&session_id=${encodeURIComponent(d.sessionId)}`);
        } else if (d.url) {
          track("checkout_started", { screen: "order-store", metadata: { storeName: store.name, paymentMethod: "card", totalPence: opts.totalPence } });
          try { localStorage.removeItem(`order_cart_${slug}`); sessionStorage.removeItem("kindness_idempotency_key"); } catch {}
          if (isPremierEmbeddedBasket && window.top) window.top.location.assign(d.url);
          else window.location.href = d.url;
          checkoutNavigating = true;
        } else {
          toast({ title: d.error ?? "Checkout failed", variant: "destructive", action: d.pending ? undefined : <ToastAction altText="Try again" onClick={() => void handleCheckout(opts)}>Try again</ToastAction> });
        }
      }
    } catch { toast({ title: "Network error", variant: "destructive", action: <ToastAction altText="Try again" onClick={() => void handleCheckout(opts)}>Try again</ToastAction> }); }
    finally { setCheckingOut(false); if (isPremierEmbeddedBasket && !checkoutNavigating) embeddedCheckoutStartedRef.current = false; }
  }

  async function handleMultiOrderStart() {
    if (!store) return;
    setCheckingOut(true);
    try {
      let token = localStorage.getItem("ayrshire_token");
      if (!token) {
        const guestRes = await fetch("/api/auth/guest", { method: "POST", headers: { "Content-Type": "application/json" } });
        if (!guestRes.ok) throw new Error("guest");
        const gd = await guestRes.json() as { token: string };
        token = gd.token;
        localStorage.setItem("ayrshire_token", token);
        localStorage.setItem("ayrshire_is_guest", "1");
        void loginWithToken(token);
      }
      const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
      // Sync anchor cart to server before showing picker
      const cartItems = cart.map((i) => ({ cartKey: i.cartKey, menuItemId: i.menuItemId, name: i.name, pricePence: i.pricePence, quantity: i.quantity, note: i.note || undefined, selectedOptions: i.selectedOptions, isFreeItem: i.isFreeItem ?? false }));
      await fetch("/api/order/basket", {
        method: "PUT",
        headers,
        body: JSON.stringify({ storeId: store.id, items: cartItems }),
      });
      navigate(`/order/multi-pick?anchorStoreId=${store.id}&anchorSlug=${slug}&anchorName=${encodeURIComponent(store.name)}`);
    } catch {
      toast({ title: "Could not start combined order", description: "Please check your connection.", variant: "destructive" });
    } finally {
      setCheckingOut(false);
    }
  }

  async function handleMultiOrderCheckout() {
    if (!store || !multiAnchorStoreId || !companionStoreIdFromUrl) return;
    setCheckingOut(true);
    try {
      let token = localStorage.getItem("ayrshire_token");
      if (!token) {
        const guestRes = await fetch("/api/auth/guest", { method: "POST", headers: { "Content-Type": "application/json" } });
        if (!guestRes.ok) throw new Error("guest");
        const gd = await guestRes.json() as { token: string };
        token = gd.token;
        localStorage.setItem("ayrshire_token", token);
        localStorage.setItem("ayrshire_is_guest", "1");
        void loginWithToken(token);
      }
      const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
      // Sync companion cart to server
      const cartItems = cart.map((i) => ({ cartKey: i.cartKey, menuItemId: i.menuItemId, name: i.name, pricePence: i.pricePence, quantity: i.quantity, note: i.note || undefined, selectedOptions: i.selectedOptions, isFreeItem: i.isFreeItem ?? false }));
      await fetch("/api/order/basket", {
        method: "PUT",
        headers,
        body: JSON.stringify({ storeId: store.id, items: cartItems }),
      });
      navigate(`/order/multi-checkout?anchorStoreId=${multiAnchorStoreId}&companionStoreId=${companionStoreIdFromUrl}&anchorSlug=${anchorSlug}&companionSlug=${slug}`);
    } catch {
      toast({ title: "Could not proceed to checkout", description: "Please check your connection.", variant: "destructive" });
    } finally {
      setCheckingOut(false);
    }
  }

  /* ── Menu item rendering ───────────────────────────────────────────
     ONE path for every item on this page. Previously the same card was
     hand-written five times (section-filter grid, section-filter list,
     section grid, section list, search results) and the copies had drifted:
     the search-result card ignored item-level discounts entirely, so a
     discounted item found via search was shown — and added — at full price.

     Layout choice follows the app:
       grid toggle → tile
       catalogue   → row  (grocery: hairline rows in one sheet)
       otherwise   → card (takeaway: real product card with imagery)   */

  /** Orders can't be added while the shop is shut, but the menu stays browsable. */
  // Premier can save a basket while closed; the shared checkout still blocks payment.
  const addDisabled = !isTestMode && store?.isOpen === false && !unifiedPremier && !isPremierWhiteLabel;

  /**
   * Sticky geometry.
   *
   * Three things pin on this page: the site navbar (64px, owned by the
   * layout), the menu search + category bar, and — on desktop — the basket
   * and category columns. The bar's height changes (the category row is
   * hidden while searching), so it is measured rather than hardcoded, the
   * same way the app measures it with `stickyHeightRef` + onLayout.
   */
  const stickyBarRef = useRef<HTMLDivElement>(null);
  const [stickyBarH, setStickyBarH] = useState(0);
  useEffect(() => {
    const el = stickyBarRef.current;
    if (!el) { setStickyBarH(0); return; }
    const measure = () => setStickyBarH(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [sections.length, menuSearch]);

  const NAVBAR_H = 64;
  /** Offset for anything that must pin *below* the menu bar. */
  const columnTop = NAVBAR_H + stickyBarH + 12;

  /**
   * Live prep-time overrides. The API sends these and the browse page already
   * consumes them, but the generated `OrderStore` type predates the fields —
   * hence the cast. Callers fall back to the configured times.
   */
  const liveMins = {
    delivery: (store as unknown as { currentDeliveryMins?: number | null } | null)?.currentDeliveryMins ?? null,
    collection: (store as unknown as { currentCollectionMins?: number | null } | null)?.currentCollectionMins ?? null,
  };

  /**
   * Minimum shown in the shop header. Uses the same helper the basket uses to
   * gate checkout, so the two can never disagree — previously the header would
   * have advertised the base minimum while the basket enforced the daypart one.
   */
  const headerMinOrder = store ? effectiveMinOrderPence(store) : 0;

  const itemLayout: "card" | "tile" | "row" =
    viewMode === "grid" ? "tile" : catalogueMode ? "row" : "card";

  /** Column counts spend desktop width on MORE items per row, not bigger ones. */
  const TILE_GRID = "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3";
  const CARD_GRID = "grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-8";
  const ROW_SHEET = cn(RADII.md, ELEVATION.sm, "bg-white border border-slate-200/80 overflow-hidden");

  function renderMenuItem(item: typeof menuItems[0], layout: "card" | "tile" | "row", showTopRule = false) {
    const isEndToEnd = (item as unknown as { isEndToEnd?: boolean }).isEndToEnd === true;
    return (
      <MenuItemCard
        key={item.id}
        className={isPremierWhiteLabel ? "premier-product-card" : undefined}
        item={item as MenuItemLike}
        layout={layout}
        qty={cartQty(item.id)}
        hasOptions={(optionGroupsMap[item.id]?.length ?? 0) > 0}
        disabled={addDisabled}
        mixMatch={mixMatchDeals}
        dense={catalogueMode}
        showFullName={isShopLocal}
        showTopRule={showTopRule}
        onAdd={(price) => handleAddClick({ ...item, pricePence: price, isEndToEnd })}
        onIncrement={() => incrementCartItem(buildCartKey(item.id, []))}
        onDecrement={() => {
          const last = cart.filter((c) => c.menuItemId === item.id).at(-1);
          if (last) removeFromCart(last.cartKey);
        }}
      />
    );
  }

  function renderItems(items: typeof menuItems, layout: "card" | "tile" | "row" = itemLayout, adSalt = 0) {
    if (items.length === 0) return null;
    // An image-led grid needs images. With none, a tile grid is a wall of
    // empty placeholder boxes, so fall back to the text-led layout.
    if (layout === "tile" && !items.some((i) => i.imageUrl)) {
      layout = catalogueMode ? "row" : "card";
    }
    // Weave a text-only advert between items roughly every this-many items. In grid
    // layouts the ad spans the full row (col-span-full) so columns stay intact.
    const MENU_AD_EVERY = 4;
    const menuAd = (adIndex: number, span: boolean) => (
      <div key={`menuad_${adIndex}`} className={span ? "col-span-full" : ""}>
        <FeedAdSlot placement="food" index={adIndex} forceFormat="textOnly" hideHouse />
      </div>
    );
    const woven = (span: boolean, render: (i: typeof menuItems[number], idx: number) => ReactElement) => {
      const nodes: ReactElement[] = [];
      items.forEach((i, idx) => {
        nodes.push(render(i, idx));
        if (!unifiedPremier && (idx + 1) % MENU_AD_EVERY === 0 && idx < items.length - 1) nodes.push(menuAd(adSalt + Math.floor(idx / MENU_AD_EVERY), span));
      });
      return nodes;
    };
    if (layout === "tile") {
      return <div className={TILE_GRID}>{woven(true, (i) => renderMenuItem(i, "tile"))}</div>;
    }
    if (layout === "row") {
      // One sheet, many rows — grouping without a rounded rectangle per SKU.
      return <div className={ROW_SHEET}>{woven(false, (i, idx) => renderMenuItem(i, "row", idx > 0))}</div>;
    }
    return <div className={CARD_GRID}>{woven(true, (i) => renderMenuItem(i, "card"))}</div>;
  }

  // Map each option excluded from an active deal → that deal's title, so the
  // option picker can warn the customer (e.g. "16 inch — Not in 2 FOR 1 ALL PIZZAS").
  const excludedOptionLabel = (() => {
    const m = new Map<number, string>();
    for (const o of ((store?.activeOffers as ActiveOffer[]) ?? [])) {
      for (const oid of (o.excludedOptionIds ?? [])) if (!m.has(oid)) m.set(oid, o.title);
    }
    return m;
  })();

  if (isLoading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" /></div>;
  if (!store) return <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center"><p className="text-slate-500 mb-4">Store not found</p><Button variant="outline" onClick={() => navigate("/order")}>Back to stores</Button></div>;

  return (
    <div className="min-h-screen bg-white">
      {showTimer && secsLeft !== null && <ClosingTimerBanner secsLeft={secsLeft} />}
      {pickerItem && (
        <OptionPickerModal
          item={pickerItem}
          groups={optionGroupsMap[pickerItem.id] ?? []}
          basePrice={pickerItem.basePrice}
          onAdd={(selections, note) => addWithOptions(pickerItem, selections, note)}
          onClose={closePicker}
          excludedOptionLabel={excludedOptionLabel}
        />
      )}

      {/* The cover remains visible behind a readable gradient; content stays in
          normal flow so long names and larger text never overflow a fixed hero. */}
      <header className="bg-white border-b border-slate-100">
        <div data-testid="shop-cover-header" className={cn("relative overflow-hidden max-w-7xl mx-auto sm:rounded-b-3xl", store.bannerUrl ? "bg-slate-900" : "bg-white")}>
          {store.bannerUrl && <>
            <img src={store.bannerUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/15 pointer-events-none" aria-hidden />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-black/20 pointer-events-none" aria-hidden />
          </>}
          <div className="absolute z-10 top-3 left-0 right-0 container mx-auto px-4 max-w-7xl pointer-events-none">
            <button
              type="button"
              onClick={() => inCompanionMode ? navigate(`/order/${anchorSlug}`) : isPremierWhiteLabel ? window.location.assign("/premier/") : navigate("/order")}
              className={cn(TYPE.caption, RADII.pill, FOCUS_RING, "pointer-events-auto inline-flex items-center gap-1.5 bg-black/55 backdrop-blur-sm text-white px-3 min-h-11 hover:bg-black/70 transition-colors cursor-pointer")}
            >
              <ArrowLeft size={14} />
              {inCompanionMode ? `Back to ${multiAnchorStoreName ?? "previous store"}` : isPremierWhiteLabel ? "Premier home" : "All stores"}
            </button>
          </div>
        <div className={cn("relative container mx-auto px-4 max-w-7xl", store.bannerUrl ? "pt-20 sm:pt-24 lg:pt-28" : "pt-14")}>
          {/* Identity — presence comes from type and spacing, not badges. */}
          <div className={`flex items-start gap-3 ${unifiedPremier ? "pt-2" : "pt-6"}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-start gap-2">
                <h1 className={cn("text-[30px] sm:text-[40px] font-bold leading-tight tracking-[-1.4px] min-w-0", store.bannerUrl ? "text-white drop-shadow-sm" : "text-[#1e3a5f]")}>{store.name}</h1>
                <button
                  type="button"
                  onClick={() => setShowStoreInfo(true)}
                  title="Shop information"
                  aria-label="Shop information"
                  className={cn(FOCUS_RING, "grid place-items-center w-10 h-10 shrink-0 rounded-full transition-colors cursor-pointer", store.bannerUrl ? "text-white bg-black/35 hover:bg-black/55" : "text-slate-400 hover:text-[#1e3a5f] hover:bg-slate-100")}
                >
                  <Info size={16} />
                </button>
              </div>

              {storeRating && (
                <div className="flex items-center gap-1 mt-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star
                      key={i}
                      size={12}
                      className={i <= Math.round(storeRating.avg) ? "text-amber-400" : "text-slate-200"}
                      fill="currentColor"
                      aria-hidden
                    />
                  ))}
                  <span className={cn(TYPE.footnote, "ml-1", store.bannerUrl ? "text-white/90" : "text-slate-500")}>
                    {storeRating.avg.toFixed(1)} · {storeRating.count} review{storeRating.count === 1 ? "" : "s"}
                  </span>
                </div>
              )}
              {unifiedPremier && store.description && (
                <p className={cn(TYPE.subhead, "mt-2 max-w-2xl", store.bannerUrl ? "text-white/90" : "text-slate-600")}>{store.description}</p>
              )}

              {/* One status / cuisine / locality line. */}
              <p className={cn(TYPE.subhead, "mt-2 flex items-center gap-1.5 flex-wrap", store.bannerUrl ? "text-white/90" : "text-slate-500")}>
                {store.isOpen === false
                  ? <Moon size={13} className={cn("shrink-0", store.bannerUrl ? "text-amber-200" : "text-red-500")} aria-hidden />
                  : <Clock size={13} className={cn("shrink-0", store.bannerUrl ? "text-emerald-200" : "text-emerald-600")} aria-hidden />}
                <span className={cn("font-semibold", store.bannerUrl ? (store.isOpen === false ? "text-amber-200" : "text-emerald-200") : (store.isOpen === false ? "text-red-600" : "text-emerald-700"))}>
                  {store.isOpen === false
                    ? (store.opensAt ? `Closed · opens ${store.opensAt}` : "Closed")
                    : (store.closesAt ? `Open until ${store.closesAt}` : "Open now")}
                </span>
                {store.category && <span>· {store.category}</span>}
                {store.town && <span>· {store.town}</span>}
              </p>
            </div>
          </div>


          {/* Fulfilment + offers — compact modules, side by side on desktop.
              Desktop width is spent on putting these in ONE row rather than
              on making any of them bigger. */}
          {(store.offersDelivery || store.offersCollection || headerMinOrder > 0 || (store.activeOffers?.length ?? 0) > 0) && (
            <div className={cn("flex flex-wrap gap-x-6 gap-y-3", unifiedPremier ? "mt-2 py-2" : "mt-5 py-4", store.bannerUrl ? "border-y border-white/20" : "border-y border-slate-100")}>
              {store.offersDelivery && (
                <div className={cn(RADII.sm, "flex items-center gap-2.5 py-1")}>
                  <Truck size={15} className={cn("shrink-0", store.bannerUrl ? "text-white" : "text-[#1e3a5f]")} aria-hidden />
                  <div className="min-w-0">
                    <p className={cn(TYPE.caption, store.bannerUrl ? "text-white/85" : "text-slate-500")}>Delivery</p>
                    <p className={cn(TYPE.subhead, "font-semibold", store.bannerUrl ? "text-white" : "text-slate-900")}>
                      {liveMins.delivery ?? store.deliveryTimeMins} min
                      {store.deliveryFeePence === 0 && !store.hasDeliveryZones
                        ? <span className={store.bannerUrl ? "text-emerald-200" : "text-emerald-700"}> · Free</span>
                        : store.hasDeliveryZones
                          ? <span className={cn("font-normal", store.bannerUrl ? "text-white/85" : "text-slate-500")}> · fee by area</span>
                          : <span className={cn("font-normal", store.bannerUrl ? "text-white/85" : "text-slate-500")}> · {pence(store.deliveryFeePence)}</span>}
                    </p>
                  </div>
                </div>
              )}
              {store.offersCollection && (
                <div className={cn(RADII.sm, "flex items-center gap-2.5 py-1")}>
                  <Package size={15} className={cn("shrink-0", store.bannerUrl ? "text-white" : "text-[#1e3a5f]")} aria-hidden />
                  <div className="min-w-0">
                    <p className={cn(TYPE.caption, store.bannerUrl ? "text-white/85" : "text-slate-500")}>Collection</p>
                    <p className={cn(TYPE.subhead, "font-semibold", store.bannerUrl ? "text-white" : "text-slate-900")}>
                      {liveMins.collection ?? store.collectionTimeMins} min
                    </p>
                  </div>
                </div>
              )}
              {headerMinOrder > 0 && (
                <div className={cn(RADII.sm, "flex items-center gap-2.5 py-1")}>
                  <ShoppingCart size={15} className={cn("shrink-0", store.bannerUrl ? "text-white" : "text-[#1e3a5f]")} aria-hidden />
                  <div className="min-w-0">
                    <p className={cn(TYPE.caption, store.bannerUrl ? "text-white/85" : "text-slate-500")}>Minimum order</p>
                    <p className={cn(TYPE.subhead, "font-semibold", store.bannerUrl ? "text-white" : "text-slate-900")}>{pence(headerMinOrder)}</p>
                  </div>
                </div>
              )}
              {(store.activeOffers?.length ?? 0) > 0 && (
                <div className={cn(RADII.sm, "flex items-start gap-2.5 border border-amber-200 bg-amber-50 px-3 py-2 col-span-2 lg:col-span-1")}>
                  <Tag size={15} className="text-amber-600 shrink-0 mt-0.5" aria-hidden />
                  <div className="min-w-0">
                    <p className={cn(TYPE.caption, "text-amber-700")}>
                      {(store.activeOffers as ActiveOffer[]).length > 1 ? "Offers" : "Offer"}
                    </p>
                    {(store.activeOffers as ActiveOffer[]).map((o, i) => (
                      <p key={i} className={cn(TYPE.subhead, "font-semibold text-amber-900")}>
                        {o.type === "free_item" ? ` ${o.title}` : formatOfferWithMin(o)}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Inline links, not settings rows. */}
          <div className={`flex items-center flex-wrap gap-x-3 gap-y-1 ${unifiedPremier ? "pt-0 pb-2" : "pt-1 pb-5"}`}>
            <button
              type="button"
              onClick={() => setShowStoreInfo(true)}
              className={cn(TYPE.footnote, FOCUS_RING, RADII.xs, "inline-flex items-center gap-1.5 font-semibold cursor-pointer min-h-11 px-3 transition-colors", store.bannerUrl ? "text-white bg-black/40 hover:bg-black/60 border border-white/20 rounded-full backdrop-blur-sm" : "text-[#1e3a5f] hover:underline")}
            >
              <Clock size={13} aria-hidden /> Hours &amp; address
            </button>
            <span className={store.bannerUrl ? "text-white/50" : "text-slate-300"} aria-hidden>·</span>
            <button
              type="button"
              onClick={() => setShowAllergyInfo(true)}
              className={cn(TYPE.footnote, FOCUS_RING, RADII.xs, "inline-flex items-center gap-1.5 font-semibold cursor-pointer min-h-11 px-3 transition-colors", store.bannerUrl ? "text-white bg-black/40 hover:bg-black/60 border border-white/20 rounded-full backdrop-blur-sm" : "text-[#1e3a5f] hover:underline")}
            >
              <AlertTriangle size={13} className="text-amber-500" aria-hidden /> Allergy info
            </button>
          </div>
        </div>
        </div>
        <div className="container mx-auto px-4 max-w-7xl"><FoodSupplierDisclosure storeId={store.id}/></div>
      </header>

      {/* Companion mode banner */}
      {inCompanionMode && multiAnchorStoreName && (
        <div className={cn(TYPE.subhead, "bg-[#1e3a5f]/10 border-b border-[#1e3a5f]/20 px-4 py-2.5 flex items-center gap-2 text-[#1e3a5f] font-medium")}>
          <ShoppingCart size={18} aria-hidden />
          <span>Combined with <strong>{multiAnchorStoreName}</strong> — add items then tap &ldquo;Continue to combined checkout&rdquo;</span>
        </div>
      )}

      {sections.length > 0 && (
        /* `top-16` clears the site navbar, which is itself `sticky top-0 z-50`
            (layout/Navbar.tsx). This bar previously used `top-0 z-20`, so it
            pinned *underneath* the navbar and was invisible once scrolled. */
        <div id={unifiedPremier ? "premier-products" : undefined} ref={stickyBarRef} className="sticky top-16 z-30 bg-white/95 backdrop-blur border-b border-slate-100 shadow-sm">
          <div className="container mx-auto px-4 max-w-7xl flex items-center gap-2 pt-2">
            <div className="relative flex-shrink-0 flex-1 max-w-xs">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={menuSearchInput}
                onChange={(e) => { setMenuSearchInput(e.target.value); if (e.target.value) { setSectionFilter(null); setSubcategoryFilter(null); } }}
                placeholder={isShopLocal ? "Search products..." : "Search menu..."}
                className="w-full pl-8 pr-3 py-1.5 text-sm rounded-full border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
              {menuSearchInput && (
                <button onClick={() => { setMenuSearchInput(""); setMenuSearch(""); }} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <X size={14} />
                </button>
              )}
            </div>
            {/* Available on every shop, as in the app — a takeaway menu is
                just as legitimately browsed as a grid. The stored preference
                key is unchanged, so existing choices carry over. */}
            <div className="flex items-center gap-1 bg-slate-100 rounded-full p-0.5 flex-shrink-0 ml-auto">
              {([["grid", LayoutGrid, "Grid view"], ["list", List, "List view"]] as const).map(([mode, Icon, label]) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => { setViewMode(mode); try { localStorage.setItem("shop_local_view_mode", mode); } catch { /* ignore */ } }}
                  title={label}
                  aria-label={label}
                  aria-pressed={viewMode === mode}
                  className={cn(
                    FOCUS_RING,
                    "w-8 h-8 rounded-full grid place-items-center transition-colors cursor-pointer",
                    viewMode === mode ? "bg-white text-[#1e3a5f] shadow-sm" : "text-slate-400 hover:text-slate-600",
                  )}
                >
                  <Icon size={14} />
                </button>
              ))}
            </div>
          </div>
          {!isPremierWhiteLabel && !menuSearch && (
            <div className="container mx-auto max-w-7xl px-4 flex items-center gap-2"><div className="flex-1 min-w-0"><PillScroller className="flex-nowrap py-2">
              {isShopLocal && !unifiedPremier && (
                <button
                  onClick={() => { setSectionFilter(null); setSubcategoryFilter(null); const el = document.getElementById("shop-local-categories"); el?.scrollIntoView({ behavior: "smooth" }); }}
                  className="whitespace-nowrap px-3.5 py-1.5 rounded-full text-sm font-semibold transition-colors flex-shrink-0 bg-slate-100 text-slate-600 hover:bg-slate-200 flex items-center gap-1.5"
                >
                  <LayoutGrid size={12} /> All
                </button>
              )}
              {(isShopLocal ? (unifiedPremier ? parentSections : sectionFilter !== null && getChildSections(sectionFilter).length > 0 ? getChildSections(sectionFilter) : parentSections) : sections).map((s) => {
                const isActive = (isShopLocal ? (subcategoryFilter ?? sectionFilter) : (activeSection ?? sections[0]?.id)) === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => scrollToSection(s.id)}
                    className={`whitespace-nowrap px-3.5 py-1.5 rounded-full text-sm font-semibold transition-colors flex-shrink-0 ${isActive ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                  >
                    {s.name}
                  </button>
                );
              })}
            </PillScroller></div><button type="button" onClick={() => setShowMenuCategories(true)} aria-label="All menu categories" className="shrink-0 w-11 h-11 rounded-2xl border border-slate-200 bg-white text-[#1e3a5f] grid place-items-center hover:bg-slate-50"><LayoutGrid size={20}/></button></div>
          )}
        </div>
      )}

      <Dialog open={showMenuCategories} onOpenChange={setShowMenuCategories}>
        <DialogContent aria-describedby={undefined} onCloseAutoFocus={(e) => e.preventDefault()} className="bg-white rounded-[28px] max-w-lg max-h-[85dvh] overflow-y-auto p-6">
          <DialogTitle className="text-2xl font-bold tracking-tight text-[#1e3a5f]">Menu categories</DialogTitle>
          <div className="grid gap-2 pt-3">{(isShopLocal ? parentSections : sections).map(section => <button key={section.id} type="button" aria-pressed={(activeSection ?? sections[0]?.id) === section.id} onClick={() => { setShowMenuCategories(false); setTimeout(() => scrollToSection(section.id), 50); }} className={cn("min-h-14 p-4 rounded-2xl border text-left text-sm font-semibold break-words", (activeSection ?? sections[0]?.id) === section.id ? "border-[#f0a500] bg-[#fff8e6] text-[#1e3a5f]" : "border-slate-200 text-slate-700 hover:bg-slate-50")}>{section.name}</button>)}</div>
        </DialogContent>
      </Dialog>

      {/* Offers now live as a module in the shop header above, beside delivery
          and collection, rather than as a full-bleed gradient band that pushed
          the menu further down the page. */}

      {isTestMode && (
        <div className="container mx-auto px-4 pt-4 max-w-7xl">
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-300 rounded-2xl px-4 py-3">
            <AlertTriangle size={16} className="text-amber-500 flex-shrink-0" />
            <div>
              <p className="font-semibold text-amber-700 text-sm">Test mode — preview only</p>
              <p className="text-xs text-amber-600">You can browse the full menu, add items and go through checkout. No order will be placed.</p>
            </div>
          </div>
        </div>
      )}
      {!isTestMode && store?.isOpen === false && (
        <div className="container mx-auto px-4 pt-4 max-w-7xl">
          <div className={cn(RADII.md, "flex items-center gap-3 bg-red-50 border border-red-200 px-4 py-3")}>
            <Moon size={16} className="text-red-500 flex-shrink-0" aria-hidden />
            <div>
              <p className={cn(TYPE.bodyStrong, "text-red-700")}>This shop is currently closed</p>
              <p className={cn(TYPE.footnote, "text-red-500")}>You can browse the menu but orders aren&apos;t being accepted right now.</p>
            </div>
          </div>
        </div>
      )}

      {showTimer && secsLeft !== null && store?.isOpen !== false && (
        <div className="container mx-auto px-4 pt-4 max-w-7xl">
          <div className={`flex items-center gap-3 rounded-2xl px-4 py-3 ${
            secsLeft < CHECKOUT_MIN_SECS
              ? "bg-red-600 text-white"
              : secsLeft < 120
              ? "bg-amber-500 text-white"
              : "bg-amber-50 dark:bg-amber-900/20 border border-amber-200 text-amber-800 dark:text-amber-300"
          }`}>
            <Clock size={16} className="flex-shrink-0" />
            <p className="font-semibold text-sm">
              {secsLeft < CHECKOUT_MIN_SECS
                ? "Store now closed — checkout unavailable"
                : `Store closes in ${fmtCountdown(secsLeft)} — complete your order soon!`}
            </p>
          </div>
        </div>
      )}

      {/* Allergy info is now an inline link in the shop header, alongside
          "Hours & address" — a full-width amber banner above every menu was
          a permanent row of chrome for a link most people tap once. */}

      {/* Store info modal */}
      {showStoreInfo && (
        <Dialog open onOpenChange={(open) => { if (!open) setShowStoreInfo(false); }}>
          {/* max-h/overflow so the seven hour rows can never push the close
              button off a short viewport. */}
          <DialogContent aria-describedby={undefined} className="bg-white rounded-3xl p-6 max-w-md max-h-[85dvh] overflow-y-auto [&>button]:min-w-11 [&>button]:min-h-11 [&>button]:grid [&>button]:place-items-center">
            <div className="flex items-center justify-between mb-4">
              <DialogTitle id="store-info-title" className="font-bold text-foreground text-lg">{store.name}</DialogTitle>
            </div>
            {(store as { address?: string | null }).address && (
              <div className="flex items-start gap-3 mb-3">
                <MapPin size={16} className="text-slate-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-slate-700">{(store as { address?: string | null }).address}{store.town ? `, ${store.town}` : ""}</p>
              </div>
            )}
            {(store as { phone?: string | null }).phone && (
              <a href={`tel:${(store as { phone?: string | null }).phone}`} className="flex items-center gap-3 mb-3 hover:text-primary">
                <Phone size={16} className="text-slate-400 flex-shrink-0" />
                <p className="text-sm font-semibold text-primary">{(store as { phone?: string | null }).phone}</p>
              </a>
            )}
            {((store as { storeLatitude?: number | null }).storeLatitude != null && (store as { storeLongitude?: number | null }).storeLongitude != null) ? (
              <a
                href={`https://maps.google.com/?q=${(store as { storeLatitude?: number | null }).storeLatitude},${(store as { storeLongitude?: number | null }).storeLongitude}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-sky-50 border border-sky-200 rounded-xl px-4 py-3 hover:bg-sky-100 transition-colors"
              >
                <MapIcon size={16} className="text-sky-600" />
                <span className="text-sm font-semibold text-sky-700">View on Map</span>
              </a>
            ) : (store as { address?: string | null }).address ? (
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(`${(store as { address?: string | null }).address}, ${store.town ?? ""}, UK`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-sky-50 border border-sky-200 rounded-xl px-4 py-3 hover:bg-sky-100 transition-colors"
              >
                <MapIcon size={16} className="text-sky-600" />
                <span className="text-sm font-semibold text-sky-700">View on Map</span>
              </a>
            ) : null}
            {/* Opening hours. The link is labelled "Hours & address" but this
                dialog only ever showed the address, phone and map — the hours
                the label promises were never rendered on web, though the app
                has shown them all along. */}
            {(() => {
              const hours = formatOpeningHours(store.openingHours);
              if (!hours) return null;
              return (
                <div className="mt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock size={16} className="text-slate-400 flex-shrink-0" aria-hidden />
                    <h4 className="text-sm font-bold text-foreground">Opening Hours</h4>
                  </div>
                  <dl>
                    {hours.map(({ label, hours: h, isToday }) => (
                      <div
                        key={label || h}
                        className={cn(
                          "flex items-baseline justify-between gap-4 rounded-md px-1.5 py-0.5",
                          isToday ? "bg-sky-50 font-bold text-sky-700" : "text-slate-600",
                        )}
                      >
                        <dt className="text-[13px]">{label}{isToday ? <span className="sr-only"> (today)</span> : null}</dt>
                        <dd className={cn("text-[13px]", isToday ? "" : "text-slate-800")}>{h}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>
      )}

      {/* Allergy info modal */}
      {showAllergyInfo && (
        <Dialog open onOpenChange={(open) => { if (!open) setShowAllergyInfo(false); }}>
          <DialogContent aria-describedby={undefined} className="bg-white rounded-3xl p-6 max-w-md max-h-[85dvh] overflow-y-auto [&>button]:min-w-11 [&>button]:min-h-11 [&>button]:grid [&>button]:place-items-center">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle size={18} className="text-amber-600" aria-hidden="true" />
                <DialogTitle id="allergy-info-title" className="font-bold text-foreground text-lg">Allergy Information</DialogTitle>
              </div>
            </div>
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-amber-900 dark:text-amber-300 leading-relaxed">If you or someone you're ordering for has a food allergy or intolerance, please do not rely on the information shown here.</p>
              <p className="text-sm text-amber-900 dark:text-amber-300 leading-relaxed mt-2">For full allergy information, please contact the shop directly before placing your order.</p>
            </div>
            {(store as { phone?: string | null }).phone ? (
              <a href={`tel:${(store as { phone?: string | null }).phone}`} className="flex items-center gap-3 bg-slate-900 text-white rounded-xl px-4 py-3 hover:bg-slate-700 transition-colors">
                <Phone size={16} />
                <span className="font-semibold text-sm">Call {store.name}</span>
                <span className="text-white/70 text-xs ml-1">{(store as { phone?: string | null }).phone}</span>
              </a>
            ) : (
              <p className="text-sm text-slate-500 text-center">No phone number on file for this shop.</p>
            )}
            <button onClick={() => setShowAllergyInfo(false)} className="w-full text-center text-sm text-slate-400 hover:text-slate-600 mt-3 py-2">Got it</button>
          </DialogContent>
        </Dialog>
      )}

      {/* One view at a time.
          These tiles ARE the categories screen. They used to stay pinned at the
          top after a category was opened, so the page showed categories above
          and that category's items below — two different things stacked, which
          is not how anyone reads a menu. Opening a category now replaces them
          with its contents, and the "All categories" control brings them back. */}
      {isShopLocal && (!unifiedPremier || showPremierFilters || groceryFiltersActive) && (sectionFilter !== null || menuSearch.trim() || groceryFiltersActive) && <div className="container mx-auto max-w-7xl px-4"><GroceryFilters value={groceryFilters} options={groceryOptions} onChange={setGroceryFilters} /></div>}
      {isShopLocal && !unifiedPremier && !groceryFiltersActive && parentSections.length > 0 && !menuSearch.trim() && sectionFilter === null && (
        <div id="shop-local-categories" className="container mx-auto px-4 pt-6 max-w-7xl">
          <h2 className="text-lg font-bold text-foreground mb-3">Browse Categories</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {parentSections.map((section) => {
              const childSects = getChildSections(section.id);
              const relevantIds = childSects.length > 0 ? childSects.map(c => c.id) : [section.id];
              const firstImage = section.imageUrl ?? menuItems.find((i) => relevantIds.includes(i.sectionId) && i.imageUrl)?.imageUrl;
              const itemCount = menuItems.filter((i) => relevantIds.includes(i.sectionId) && i.available !== false).length;
              return (
                <button
                  key={section.id}
                  onClick={() => { setSectionFilter(section.id); setSubcategoryFilter(null); setTimeout(() => { document.getElementById(`section-${section.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }); }, 50); }}
                  className={`premier-category-card group relative rounded-2xl overflow-hidden aspect-square bg-slate-100 hover:scale-[1.02] transition-transform shadow-sm hover:shadow-md ${sectionFilter === section.id ? "ring-2 ring-primary" : ""}`}
                >
                  {firstImage ? (
                    <img src={firstImage} alt={section.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async" />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-primary/40" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-2.5">
                    <p className="text-white font-bold text-xs leading-tight line-clamp-2 drop-shadow">{section.name}</p>
                    <p className="text-white/70 text-[10px] mt-0.5">{itemCount} item{itemCount !== 1 ? "s" : ""}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-6 max-w-7xl">
        <div className={`flex gap-8 items-start ${isShopLocal ? "lg:gap-6" : ""}`}>
          {/* Left category sidebar — Shop Local desktop only */}
          {isShopLocal && sectionFilter !== null && parentSections.length > 0 && !menuSearch.trim() && (
            <div
              role="navigation" aria-label="Shop categories"
              className="hidden lg:block w-48 xl:w-56 flex-shrink-0 sticky overflow-y-auto"
              style={{ top: columnTop, maxHeight: `calc(100vh - ${columnTop + 16}px)`, scrollbarWidth: "thin" }}
            >
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 px-2">Categories</p>
              <div className="flex flex-col gap-0.5">
                {!unifiedPremier && <button
                  onClick={() => { setSectionFilter(null); setSubcategoryFilter(null); }}
                  className={`text-left px-3 py-2 rounded-xl text-sm transition-colors font-medium ${sectionFilter === null ? "bg-primary/10 text-primary font-semibold" : "text-slate-500 hover:bg-slate-100"}`}
                >
                  All categories
                </button>}
                {parentSections.map((s) => {
                  const isActive = sectionFilter === s.id;
                  return (
                    <button
                      key={s.id}
                    onClick={() => scrollToSection(s.id)}
                      className={`text-left px-3 py-2 rounded-xl text-sm transition-colors font-medium ${isActive ? "bg-primary/10 text-primary font-semibold" : "text-slate-600 hover:bg-slate-100"}`}
                    >
                      {s.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="flex-1 min-w-0">
            {unifiedPremier && <section id="premier-deals" className="premier-deals-inline" aria-label="Premier deals">
              <div><Tag size={16} aria-hidden /><strong>Deals</strong></div>
              {(store.activeOffers as ActiveOffer[] | undefined)?.length ? (
                <p>{(store.activeOffers as ActiveOffer[]).slice(0, 2).map(o => o.title).join(" · ")}</p>
              ) : <p>No live deals today</p>}
            </section>}
            {groceryFiltersActive ? <div aria-live="polite"><p className="text-sm text-slate-600 mb-3">{groceryResults.length} matching product{groceryResults.length === 1 ? "" : "s"}</p>{groceryResults.length ? renderItems(groceryResults) : <p>No products match these filters. Try another size or clear filters.</p>}</div> : sections.length === 0 ? (
              <div className="bg-white rounded-2xl p-10 text-center border border-slate-100"><p className="text-slate-400">Menu coming soon</p></div>
            ) : menuSearch.trim() || !isShopLocal ? null : sectionFilter !== null ? (
              /* Section filter active — subcategory tiles or items */
              (() => {
                const section = sections.find((s) => s.id === sectionFilter);
                if (!section) return null;
                const childSects = getChildSections(sectionFilter);
                const hasChildren = subcategoriesEnabled && childSects.length > 0;

                /* Subcategory tile grid */
                if (!unifiedPremier && hasChildren && subcategoryFilter === null) {
                  return (
                    <div id={`section-${section.id}`}>
                      <div className="flex items-center gap-3 mb-4">
                        <button onClick={() => { setSectionFilter(null); setSubcategoryFilter(null); }} className="flex items-center gap-1 text-sm text-primary font-semibold hover:underline">
                          <ArrowLeft size={14} /> All categories
                        </button>
                        <span className="text-slate-300">/</span>
                        <span className="text-sm font-bold text-foreground">{section.name}</span>
                        <span className="text-xs text-slate-400 ml-auto">{childSects.length} subcategor{childSects.length !== 1 ? "ies" : "y"}</span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {childSects.map((child) => {
                          const childItems = menuItems.filter((i) => i.sectionId === child.id && i.available !== false);
                          const childImage = child.imageUrl ?? childItems.find((i) => i.imageUrl)?.imageUrl;
                          return (
                            <button
                              key={child.id}
                              onClick={() => setSubcategoryFilter(child.id)}
                              className="premier-category-card group relative rounded-2xl overflow-hidden aspect-square bg-slate-100 hover:scale-[1.02] transition-transform shadow-sm hover:shadow-md"
                            >
                              {childImage ? (
                                <img src={childImage} alt={child.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async" />
                              ) : (
                                <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-primary/40" />
                              )}
                              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                              <div className="absolute bottom-0 left-0 right-0 p-2.5">
                                <p className="text-white font-bold text-xs leading-tight line-clamp-2 drop-shadow">{child.name}</p>
                                <p className="text-white/70 text-[10px] mt-0.5">{childItems.length} item{childItems.length !== 1 ? "s" : ""}</p>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                }

                /* Items view (subcategoryFilter set, or no children) */
                const activeId = subcategoryFilter ?? sectionFilter;
                const activeSection = subcategoryFilter ? sections.find((s) => s.id === subcategoryFilter) : section;
                const descendantIds = new Set([activeId]);
                if (unifiedPremier && subcategoryFilter === null) {
                  let added = true;
                  while (added) {
                    added = false;
                    for (const child of sections) if (descendantIds.has(child.parentSectionId ?? -1) && !descendantIds.has(child.id)) { descendantIds.add(child.id); added = true; }
                  }
                }
                const sectionItems = menuItems.filter((i) => descendantIds.has(i.sectionId) && i.available !== false);
                const visibleSectionItems = unifiedPremier ? sectionItems.slice(0, premierVisibleCount) : sectionItems;
                return (
                  <div>
                    {unifiedPremier ? <div className="flex items-center justify-between gap-3 mb-4"><h2 className="text-xl font-bold text-slate-900">{section.name}</h2><div className="flex items-center gap-2"><span className={cn(TYPE.footnote, "text-slate-500")}>{sectionItems.length} products</span><button type="button" className="premier-filter-toggle" aria-expanded={showPremierFilters} onClick={() => setShowPremierFilters(v => !v)}>Filters</button></div></div> : <div className="flex items-center gap-3 mb-4">
                      {subcategoryFilter !== null ? (
                        <button onClick={() => setSubcategoryFilter(null)} className={cn(TYPE.subhead, FOCUS_RING, RADII.xs, "flex items-center gap-1 text-[#1e3a5f] font-semibold hover:underline cursor-pointer")}>
                          <ArrowLeft size={14} /> {section.name}
                        </button>
                      ) : (
                        <button onClick={() => { setSectionFilter(null); setSubcategoryFilter(null); }} className={cn(TYPE.subhead, FOCUS_RING, RADII.xs, "flex items-center gap-1 text-[#1e3a5f] font-semibold hover:underline cursor-pointer")}>
                          <ArrowLeft size={14} /> All categories
                        </button>
                      )}
                      <span className="text-slate-300" aria-hidden>/</span>
                      <span className={cn(TYPE.subhead, "font-bold text-slate-900")}>{activeSection?.name}</span>
                      <span className={cn(TYPE.footnote, "text-slate-400 ml-auto")}>{sectionItems.length} item{sectionItems.length !== 1 ? "s" : ""}</span>
                    </div>}
                    {sectionItems.length === 0 ? (
                      <div className={cn(RADII.md, "bg-white border border-slate-200/80 p-10 text-center")}>
                        <p className={cn(TYPE.callout, "text-slate-400")}>Nothing here yet</p>
                      </div>
                    ) : renderItems(visibleSectionItems)}
                    {unifiedPremier && sectionItems.length > premierVisibleCount && <button type="button" onClick={() => setPremierVisibleCount(c => c + 24)} className="premier-show-more">Show more {section.name.toLowerCase()} products</button>}
                  </div>
                );
              })()
            ) : isShopLocal ? (
              /* Nothing here. The tile grid above already IS the no-filter view;
                 this rendered the identical categories a second time as a list,
                 so every shop with subcategories showed them twice. */
              null
            ) : null}
            {!groceryFiltersActive && menuSearch.trim() ? (() => {
              const q = menuSearch.trim().toLowerCase();
              const sectionNameMap: Record<number, string> = Object.fromEntries(sections.map(s => [s.id, s.name.toLowerCase()]));
              const results = menuItems.filter((i) =>
                i.name.toLowerCase().includes(q) ||
                (i.description ?? "").toLowerCase().includes(q) ||
                (sectionNameMap[i.sectionId] ?? "").includes(q)
              );
              if (results.length === 0) {
                return (
                  <div className={cn(RADII.md, "bg-white p-10 text-center border border-slate-200/80 flex flex-col items-center gap-2")}>
                    <Search size={32} className="text-slate-300" aria-hidden />
                    <p className={cn(TYPE.bodyStrong, "text-slate-600")}>No items found</p>
                    <p className={cn(TYPE.callout, "text-slate-400")}>
                      Nothing on this menu matches &ldquo;{menuSearch.trim()}&rdquo;.
                    </p>
                    <button
                      type="button"
                      onClick={() => { setMenuSearchInput(""); setMenuSearch(""); }}
                      className={cn(TYPE.caption, RADII.pill, FOCUS_RING, "mt-1 px-3 h-9 border border-slate-200 text-[#1e3a5f] hover:bg-slate-50 transition-colors cursor-pointer")}
                    >
                      Clear search
                    </button>
                  </div>
                );
              }
              const grouped = sections.map(s => ({ section: s, items: results.filter(i => i.sectionId === s.id) })).filter(g => g.items.length > 0);
              return (
                <div>
                  <p className={cn(TYPE.footnote, "text-slate-500 font-semibold mb-3")}>
                    {results.length} result{results.length !== 1 ? "s" : ""} for &ldquo;{menuSearch.trim()}&rdquo;
                  </p>
                  {grouped.map(({ section, items }) => {
                    const available = items.filter(i => i.available);
                    if (available.length === 0) return null;
                    return (
                      <div key={section.id} className="mb-6">
                        <h2 className={cn(TYPE.overline, "text-slate-400 mb-2 px-1")}>{section.name}</h2>
                        {renderItems(available)}
                      </div>
                    );
                  })}
                </div>
              );
            })() : isShopLocal ? null : sections.map((section, sectionIdx) => {
              const sectionItems = menuItems.filter((i) => i.sectionId === section.id);
              const collapsed = collapsedSections.has(section.id);
              return (
                <div key={section.id} id={`section-${section.id}`} className="mb-6 scroll-mt-32">
                  <button
                    type="button"
                    onClick={() => toggleSection(section.id)}
                    aria-expanded={!collapsed}
                    className={cn(FOCUS_RING, RADII.xs, "w-full flex items-center justify-between gap-3 mb-3 group cursor-pointer text-left")}
                  >
                    <div className="min-w-0">
                      <h2 className={cn("text-[25px] font-bold tracking-[-0.8px] text-[#1e3a5f]")}>{section.name}</h2>
                      {section.description && <p className={cn(TYPE.footnote, "text-slate-500 mt-0.5")}>{section.description}</p>}
                    </div>
                    {collapsed
                      ? <ChevronDown size={18} className="text-slate-400 shrink-0" aria-hidden />
                      : <ChevronUp size={18} className="text-slate-400 shrink-0" aria-hidden />}
                  </button>
                  {!collapsed && renderItems(sectionItems, itemLayout, sectionIdx * 3)}
                </div>
              );
            })}
          </div>

          {/* Keep checkout below the category bar, with more room for the
              single-panel takeaway form. Retail keeps its existing sidebar
              proportions alongside its extra category column. */}
          <div
            className={cn("hidden lg:block flex-shrink-0 sticky overflow-y-auto", isShopLocal ? "w-80 2xl:w-[22rem]" : "w-[22rem] xl:w-96")}
            style={{ top: columnTop, maxHeight: `calc(100vh - ${columnTop + 16}px)` }}
          >
            <div className={cn(RADII.md, ELEVATION.sm, "bg-white border border-slate-200/80")}>
              <div className="px-5 pt-4 pb-3 sticky top-0 bg-white z-10 border-b border-slate-100 rounded-t-[14px]">
                <h2 className={cn(TYPE.title3, "text-slate-900 flex items-center gap-2")}>
                  <ShoppingCart size={17} className="text-[#1e3a5f]" aria-hidden /> Your order
                  {cartCount > 0 && <Badge className="ml-auto bg-[#1e3a5f] text-white">{cartCount}</Badge>}
                </h2>
              </div>
              <div className="p-5 pt-3">
                <CartPanel
                  cart={cart} store={store} deals={mixMatchDeals}
                  activeOffers={(store?.activeOffers as ActiveOffer[]) ?? []}
                  menuItems={menuItems}
                  onAdd={handleCartAdd}
                  onRemove={removeFromCart}
                  onUpdateNote={(cartKey, note) => setCart((prev) => prev.map((c) => c.cartKey === cartKey ? { ...c, note: note || undefined } : c))}
                  onCheckout={startCheckout}
                  kindnessContributionPence={kindnessContributionPence}
                  onKindnessChange={kindnessConfig ? () => setKindnessModalOpen(true) : undefined}
                  onKindnessRemove={kindnessConfig ? () => setKindnessContributionPence(0) : undefined}
                  kindnessCreditPreviewPence={kindnessCreditPreviewPence}
                  loading={checkingOut}
                  checkoutBlockedByTimer={checkoutBlockedByTimer}
                  closedPayLabel={isPremierWhiteLabel && store.isOpen === false ? nextOpeningPayLabel(store.openingHours) : undefined}
                  isTestMode={isTestMode}
                  initialDiscountCode={initialDiscountCode}
                  onNameChange={(n) => saveGuestContact({ name: n })}
                  onPhoneChange={(p) => saveGuestContact({ phone: p })}
                  showGuestEmail={!user || (user as { role?: string }).role === "guest"}
                  guestEmail={guestContact.email}
                  onEmailChange={(e) => { setGuestContact((p) => ({ ...p, email: e })); saveGuestContact({ email: e }); }}
                  inCompanionMode={inCompanionMode}
                  multiAnchorName={multiAnchorStoreName ?? undefined}
                  onMultiOrderStart={!isPremierWhiteLabel && !inCompanionMode && store.useHubDrivers ? () => void handleMultiOrderStart() : undefined}
                  onMultiOrderCheckout={inCompanionMode ? () => void handleMultiOrderCheckout() : undefined}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {cartCount > 0 && (
        <div className="lg:hidden fixed bottom-6 left-4 right-4 z-50">
          <button onClick={openCart} className="w-full bg-primary text-white rounded-2xl px-5 py-4 flex items-center justify-between shadow-xl font-semibold">
            <span className="bg-white/20 rounded-lg w-7 h-7 flex items-center justify-center text-sm font-bold">{cartCount}</span>
            <span>{inCompanionMode ? "View basket" : "View basket"}</span>
            <span>{pence(cartTotal)}</span>
          </button>
        </div>
      )}

      {cartOpen && (
        <Dialog open onOpenChange={(open) => { if (!open) { if (isPremierEmbeddedBasket && embeddedCheckoutStartedRef.current) return; setCartOpen(false); if (isPremierEmbeddedBasket) window.parent.postMessage({ type: "premier:basket-close" }, window.location.origin); } }}>
          <DialogContent aria-describedby={undefined} className="food-sheet bg-white bottom-0 top-auto left-0 translate-x-0 translate-y-0 sm:left-1/2 sm:top-1/2 sm:bottom-auto sm:-translate-x-1/2 sm:-translate-y-1/2 w-full max-w-[600px] flex flex-col gap-4 rounded-t-[32px] sm:rounded-[32px] px-5 pb-[max(24px,env(safe-area-inset-bottom))] pt-3 h-[94dvh] sm:h-[90dvh] max-h-[94dvh] sm:max-h-[92dvh] overflow-y-auto [&>button]:min-h-11 [&>button]:min-w-11 [&>button]:grid [&>button]:place-items-center">
            <FoodSheetHandle onDismiss={() => { setCartOpen(false); if (isPremierEmbeddedBasket) window.parent.postMessage({ type: "premier:basket-close" }, window.location.origin); }} />
            <div className="sr-only">
              <DialogTitle>Your basket</DialogTitle>
            </div>
            <CartPanel
              compactMobileBasket
              cart={cart} store={store} deals={mixMatchDeals}
              activeOffers={(store?.activeOffers as ActiveOffer[]) ?? []}
              menuItems={menuItems}
              onAdd={handleCartAdd}
              onRemove={removeFromCart}
              onUpdateNote={(cartKey, note) => setCart((prev) => prev.map((c) => c.cartKey === cartKey ? { ...c, note: note || undefined } : c))}
              onCheckout={(opts) => { if (isPremierEmbeddedBasket) embeddedCheckoutStartedRef.current = true; else setCartOpen(false); startCheckout(opts); }}
              kindnessContributionPence={kindnessContributionPence}
              onKindnessChange={kindnessConfig ? () => setKindnessModalOpen(true) : undefined}
              onKindnessRemove={kindnessConfig ? () => setKindnessContributionPence(0) : undefined}
              kindnessCreditPreviewPence={kindnessCreditPreviewPence}
              loading={checkingOut}
              checkoutBlockedByTimer={checkoutBlockedByTimer}
              closedPayLabel={isPremierWhiteLabel && store.isOpen === false ? nextOpeningPayLabel(store.openingHours) : undefined}
              isTestMode={isTestMode}
              initialDiscountCode={initialDiscountCode}
              onNameChange={(n) => saveGuestContact({ name: n })}
              onPhoneChange={(p) => saveGuestContact({ phone: p })}
              showGuestEmail={!user || (user as { role?: string }).role === "guest"}
              guestEmail={guestContact.email}
              onEmailChange={(e) => { setGuestContact((p) => ({ ...p, email: e })); saveGuestContact({ email: e }); }}
              inCompanionMode={inCompanionMode}
              multiAnchorName={multiAnchorStoreName ?? undefined}
              onMultiOrderStart={!isPremierWhiteLabel && !inCompanionMode && store.useHubDrivers ? () => { setCartOpen(false); void handleMultiOrderStart(); } : undefined}
              onMultiOrderCheckout={inCompanionMode ? () => { setCartOpen(false); void handleMultiOrderCheckout(); } : undefined}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Random Act of Kindness — branded modal shown once per checkout attempt */}
      {kindnessConfig && (
        <KindnessModal
          open={kindnessModalOpen}
          config={kindnessConfig}
          onResolve={handleKindnessResolve}
        />
      )}

      {/* Random Act of Kindness — RECIPIENT thank-you modal. Shown ONCE per store
          visit while browsing (page-level, so CartPanel's two mounts never pop it
          twice) when a previous donor has paid forward towards this order. */}
      {kindnessCreditPreviewPence > 0 && (
        <Dialog open={kindnessRecipientModalOpen} onOpenChange={setKindnessRecipientModalOpen}>
          <DialogContent className="max-w-md rounded-3xl max-h-[92dvh] overflow-y-auto [&>button]:min-h-11 [&>button]:min-w-11 [&>button]:grid [&>button]:place-items-center">
            <DialogHeader>
              <DialogTitle className="text-primary">Random Act of Kindness </DialogTitle>
              <DialogDescription className="space-y-1.5">
                <span className="block">
                  {kindnessDonorName
                    ? `${kindnessDonorName} has paid ${pence(kindnessCreditPreviewPence)} towards your order `
                    : `Someone has paid ${pence(kindnessCreditPreviewPence)} towards your order `}
                </span>
                <span className="block">It'll come off your order automatically — reserved for 10 minutes once you check out.</span>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                className="bg-secondary hover:bg-secondary/90 text-white font-bold"
                onClick={() => setKindnessRecipientModalOpen(false)}
              >
                Let&apos;s order!
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
